import { planConfigs } from "@postdaily/api/services/plan-limits";
import { xPostsPerMonth } from "@postdaily/api/services/x-credit-rates";
import { env } from "@postdaily/env/web";
import type { IsoDate } from "@/lib/content-date";
import { joinWithAnd } from "@/lib/join-with-and";
import type { CrossLink } from "./cross-links";
/**
 * Help-centre content. Mirrors the `blog-data.ts` pattern (in-code content,
 * rendered by `help-article-page.tsx`, read by slug from `/help/$slug`) so
 * there is one convention for marketing content until a CMS lands.
 *
 * Each article is the step-by-step twin of a guide video: the `video` field
 * points at the produced clip, and the body repeats the same steps as text so
 * the page is useful (and indexable) before the video exists and for people who
 * would rather read. Scripts + storyboards live in
 * `docs/guides/facebook-video-scripts.md`.
 */

export type HelpCategory =
  | "Getting started"
  | "Connecting channels"
  | "Scheduling & queue"
  | "Composer & AI"
  | "AI agents"
  | "Analytics & reporting"
  | "Team & workspace"
  | "Account & security"
  | "Billing & plans"
  | "Troubleshooting";

/** Ordered list driving the help index grid. */
export const HELP_CATEGORIES: HelpCategory[] = [
  "Getting started",
  "Connecting channels",
  "Scheduling & queue",
  "Composer & AI",
  "AI agents",
  "Analytics & reporting",
  "Team & workspace",
  "Account & security",
  "Billing & plans",
  "Troubleshooting",
];

/**
 * Intrinsic size of every help screenshot.
 *
 * `scripts/guide-capture/shots.mjs` captures at a fixed viewport and device
 * scale and then downscales to WebP, so all 75 are 1920x1080. Declaring it
 * lets the browser reserve the right box before the image arrives — without it
 * each screenshot shoved the article down as it loaded, which is layout shift
 * on the most-crawled pages on the site.
 *
 * 1920 is two and a half times the ~760px column these render in, so they stay
 * crisp on a retina display without shipping the 4K capture. The PNGs those
 * came from averaged 707 KB each and 51 MB in total; the WebPs average 58 KB
 * and 4.3 MB.
 *
 * A single pair rather than per-image data because the capture is uniform;
 * `help-images.test.ts` asserts that stays true, so a change in the capture
 * script fails a test instead of silently breaking every article's layout.
 */
export const HELP_SCREENSHOT_WIDTH = 1920;
export const HELP_SCREENSHOT_HEIGHT = 1080;

/** A block of help body content. Paragraph/bullet/step text supports `**bold**`. */
export type HelpBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "ul"; items: string[] }
  /**
   * Numbered steps. `start` continues the count across interleaved images, so a
   * walkthrough can put a screenshot after every single step without the
   * numbering restarting at 1 each time.
   */
  | { type: "steps"; start?: number; items: { title: string; text?: string }[] }
  | { type: "note"; tone: "info" | "warning"; title: string; text: string }
  /**
   * An annotated screenshot from `apps/web/public/help/<slug>/`.
   *
   * Captured by `scripts/guide-capture/shots.mjs`, which draws the numbered
   * red callouts in the page before screenshotting — so a highlight can never
   * drift away from the control it points at. `alt` is required: these images
   * carry instructions, and a reader on a screen reader needs them too.
   */
  | { type: "image"; src: string; alt: string; caption?: string };

/**
 * Guide videos live in the media bucket under a PUBLIC `guides/` prefix and are
 * served by the API worker, not shipped as web assets — 60 MB of video in the
 * deploy bundle would slow every unrelated web release, and grow with each new
 * guide. See the `/guides/:collection/:file` route in apps/server.
 */
function guideVideo(collection: string, slug: string, caption: string) {
  const base = `${env.VITE_SERVER_URL.replace(/\/$/, "")}/guides/${collection}/${slug}`;

  return { caption, poster: `${base}.jpg`, src: `${base}.mp4` };
}

export type HelpArticle = {
  slug: string;
  title: string;
  excerpt: string;
  category: HelpCategory;
  /** Last-updated date, ISO 8601. Formatted for display at render time. */
  updated: IsoDate;
  /** Display read time, e.g. "3 min". */
  read: string;
  /**
   * Guide video for this article, served from `apps/web/public/videos/`.
   * Omitted until the clip is produced — the page renders fine without it.
   */
  video?: { src: string; poster?: string; caption: string };
  body: HelpBlock[];
  /** Slugs of articles shown in the "Related" rail. */
  related?: string[];
  /** Guides, tools and pages beyond the help centre (see `cross-links.ts`). */
  links?: readonly CrossLink[];
};

/*
  The X budget in plain terms, from the plans data and X's rates, so the help
  article can't promise more posts than a plan's credit budget covers (see
  `pricing-faqs.ts`, which quotes the same figures).
*/
const X_BUDGET_PLANS = (["starter", "pro", "agency"] as const).map((code) => ({
  name: planConfigs[code].name,
  ...xPostsPerMonth(planConfigs[code].limits.maxXCreditsPerMonth),
}));
const X_BUDGET_TEXT = `Each paid plan includes a monthly budget for X, which covers about ${joinWithAnd(
  X_BUDGET_PLANS.map((plan) => `${plan.plain} posts on ${plan.name}`),
)}, or ${joinWithAnd(
  X_BUDGET_PLANS.map((plan) => `${plan.withLink} on ${plan.name}`),
)} if every post carries a link. The budget belongs to the workspace owner and is shared by all their workspaces.`;

const FACEBOOK_ARTICLES: HelpArticle[] = [
  {
    slug: "connect-a-facebook-page",
    links: [
      {
        blog: "schedule-facebook-posts-without-business-suite",
        label: "Schedule Facebook Page posts without Meta Business Suite",
      },
    ],
    title: "Connect a Facebook Page",
    excerpt:
      "Authorize Facebook, choose which Pages to add to PostDaily, and find out why a Page you manage might be missing from the list, and how to fix it.",
    category: "Connecting channels",
    updated: "2026-08-14",
    read: "3 min",
    video: guideVideo(
      "facebook",
      "connect-a-facebook-page",
      "Connecting a Facebook Page, from the channel grid to the Page picker.",
    ),
    body: [
      {
        type: "p",
        text: "PostDaily publishes to Facebook **Pages**, not personal profiles. That's a Facebook rule, not ours — the Pages API is the only way any tool can publish to Facebook on a schedule. If you only have a personal profile, create a Page first, then come back here.",
      },
      {
        type: "p",
        text: "The whole thing takes about a minute. Follow along with the screenshots below.",
      },
      { type: "h2", text: "Connect your Page" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Open Channels, then Connect channel",
            text: "You'll find both in the left sidebar.",
          },
        ],
      },
      {
        type: "image",
        src: "/help/connect-a-facebook-page/01-sidebar-connect.webp",
        alt: "The PostDaily sidebar with the Connect channel link highlighted.",
        caption: "Connect channel sits under your list of channels.",
      },
      {
        type: "steps",
        start: 2,
        items: [
          {
            title: "Find Facebook and click Connect",
            text: "Every network PostDaily supports appears as a card on this page.",
          },
        ],
      },
      {
        type: "image",
        src: "/help/connect-a-facebook-page/02-choose-facebook.webp",
        alt: "The connect page showing provider cards, with the Connect button on the Facebook card highlighted.",
      },
      {
        type: "steps",
        start: 3,
        items: [
          {
            title: "Click Continue with Facebook",
            text: "PostDaily explains what it's about to ask for, then hands you over to Facebook.",
          },
        ],
      },
      {
        type: "image",
        src: "/help/connect-a-facebook-page/03-continue-with-facebook.webp",
        alt: "The Facebook connect dialog with the Continue with Facebook button highlighted.",
      },
      {
        type: "steps",
        start: 4,
        items: [
          {
            title: "Approve the permissions on Facebook",
            text: "Facebook asks which Pages to grant. Tick every Page you want to post to — whatever you tick here is all PostDaily can ever see.",
          },
          {
            title: "Choose what to add",
            text: "Back in PostDaily you'll see the Pages you granted. Tick the ones you want as channels and confirm.",
          },
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "One channel per Page",
        text: "PostDaily creates a separate channel for every Page you select, each with its own posting schedule, queue and analytics. Only add the Pages you'll actually post to — each one uses a channel slot on your plan.",
      },
      {
        type: "p",
        text: "That's it. Your Page now appears in Channels, ready to schedule.",
      },
      {
        type: "image",
        src: "/help/connect-a-facebook-page/04-page-connected.webp",
        alt: "The connect page showing the Facebook card with a connected Page listed as active.",
        caption: "A connected Page shows as active and is ready to publish.",
      },
      { type: "h2", text: "A Page is missing from the list" },
      {
        type: "p",
        text: "This is the most common question we get, and it almost always has the same cause: the Page wasn't granted during the **Facebook** step, so PostDaily never received it.",
      },
      {
        type: "ul",
        items: [
          "Reconnect the channel and, on Facebook's screen, make sure the Page is ticked.",
          "You need to be an **admin** of the Page — editor and moderator roles can't grant publishing access.",
          "Pages in a Business Portfolio may need you to authorize through the right portfolio.",
        ],
      },
      {
        type: "note",
        tone: "warning",
        title: "Publishing to a personal profile isn't possible",
        text: "Facebook's API does not allow scheduled publishing to personal timelines from any third-party tool. This is a Facebook restriction that applies to every scheduler, not just PostDaily.",
      },
    ],
    related: ["publish-your-first-facebook-post", "when-a-post-fails"],
  },
  {
    slug: "publish-your-first-facebook-post",
    title: "Publish your first Facebook post",
    excerpt:
      "Write a Facebook Page post, preview it exactly as Facebook will render it, attach photos and a link, then publish it now or schedule it for later.",
    category: "Getting started",
    updated: "2026-08-14",
    read: "4 min",
    video: guideVideo(
      "facebook",
      "publish-your-first-facebook-post",
      "Writing, formatting and scheduling a first Facebook post.",
    ),
    body: [
      {
        type: "p",
        text: "The composer works the same way for every network. You pick which channels a post goes to, write once, and each channel gets its own preview and its own settings.",
      },
      { type: "h2", text: "Write your post" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Click New post",
            text: "It's at the top of the Queue, Calendar and Dashboard — wherever you are.",
          },
        ],
      },
      {
        type: "image",
        src: "/help/publish-your-first-facebook-post/01-new-post.webp",
        alt: "The queue page with the New post button highlighted.",
      },
      {
        type: "steps",
        start: 2,
        items: [
          {
            title: "Pick the Page you're publishing to",
            text: "Click your Facebook Page's avatar. The counter on the right tracks how many channels the post will go to, so it ticks up to **1 of** however many you have connected. Nothing else unlocks until at least one is picked, so a post can never go out to the wrong channel by accident.",
          },
        ],
      },
      {
        type: "image",
        src: "/help/publish-your-first-facebook-post/02-select-channel.webp",
        alt: "The composer with a Facebook Page selected and the channel counter highlighted.",
      },
      {
        type: "steps",
        start: 3,
        items: [
          {
            title: "Write your caption",
            text: "The live preview on the right shows exactly how the post will look on Facebook, updating as you type.",
          },
        ],
      },
      {
        type: "image",
        src: "/help/publish-your-first-facebook-post/03-write-and-preview.webp",
        alt: "The composer with a caption typed and the live Facebook preview highlighted.",
        caption: "What you see here is what your followers see.",
      },
      { type: "h2", text: "Choose a format" },
      {
        type: "p",
        text: "Above the editor you'll find three formats. Each has its own rules, and the preview changes to match.",
      },
      {
        type: "image",
        src: "/help/publish-your-first-facebook-post/04-post-formats.webp",
        alt: "The composer's format selector showing Post, Reel and Story options, all highlighted.",
      },
      {
        type: "ul",
        items: [
          "**Post** — a normal feed post. Text on its own, or up to 10 photos, or a video.",
          "**Reel** — vertical video only. If you have an image attached, this option is disabled and tells you why.",
          "**Story** — a single vertical photo or video that disappears after 24 hours.",
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "Formats move into the editor once you add media",
        text: "With no media attached the format row sits above the text area. Attach a photo or video and the controls move up beside your media, so the format and the file it applies to stay together.",
      },
      { type: "h2", text: "Add a first comment" },
      {
        type: "p",
        text: "Links in a Facebook post can cost you reach, so many Pages put the link in the first comment instead. Click **Add comment** and PostDaily posts it the instant your post goes live — from the same Page, with no reminder needed.",
      },
      {
        type: "image",
        src: "/help/publish-your-first-facebook-post/05-first-comment.webp",
        alt: "The composer with the Add comment button highlighted below the editor.",
      },
      {
        type: "note",
        tone: "warning",
        title: "First comments need the Pages engagement permission",
        text: "If you connected your Page before this permission was requested, first comments won't publish. Reconnect the channel once and it works from then on.",
      },
      { type: "h2", text: "Choose how it goes out" },
      {
        type: "p",
        text: "The button in the bottom corner shows the default — the next open slot in your posting schedule. Click the arrow beside it to see every option.",
      },
      {
        type: "image",
        src: "/help/publish-your-first-facebook-post/06-submit-options.webp",
        alt: "The submit button expanded, showing Next available, Post now, and Pick date and time.",
      },
      {
        type: "ul",
        items: [
          "**Next available** — drops the post into the next free slot in your queue.",
          "**Post now** — publishes immediately and reports the result inline.",
          "**Pick date & time** — schedules it for an exact moment you choose.",
        ],
      },
      {
        type: "p",
        text: "Not ready yet? **Save draft** keeps everything exactly as it is, and the draft is waiting for you in your queue.",
      },
    ],
    related: [
      "facebook-reels-and-stories",
      "facebook-first-comment",
      "facebook-scheduling-and-queue",
    ],
  },
  {
    slug: "facebook-reels-and-stories",
    links: [
      {
        blog: "how-to-schedule-facebook-posts",
        label: "How to schedule Facebook posts, Reels and Stories",
      },
      {
        blog: "facebook-scheduled-post-not-posting",
        label: "Facebook scheduled post didn't post? Every cause",
      },
    ],
    title: "Facebook Reels & Stories in PostDaily",
    excerpt:
      "Schedule Facebook Reels and Stories: switch post format, use the 9:16 preview, tag a collaborating Page, and avoid the video formats Facebook rejects.",
    category: "Composer & AI",
    updated: "2026-08-14",
    read: "4 min",
    video: guideVideo(
      "facebook",
      "facebook-reels-and-stories",
      "Choosing between a feed post, a Reel and a Story.",
    ),
    body: [
      {
        type: "p",
        text: "Facebook isn't only feed posts. Above the editor you choose a **format** — Post, Reel or Story — and PostDaily changes the preview and the available options to match.",
      },
      { type: "h2", text: "Choosing a format" },
      {
        type: "p",
        text: "Attach your media first. The format options appear beside it, and which ones you can pick depends on what you attached.",
      },
      {
        type: "image",
        src: "/help/facebook-reels-and-stories/01-formats-with-video.webp",
        alt: "The composer with a video attached, showing the Post, Reel and Story format options.",
      },
      {
        type: "ul",
        items: [
          "**Post** — a normal feed post. Text alone, up to 10 photos, or a video.",
          "**Reel** — vertical video only. With an image attached this option is disabled, and hovering it tells you why.",
          "**Story** — a single vertical photo or video that disappears after 24 hours.",
        ],
      },
      { type: "h2", text: "Reels" },
      {
        type: "p",
        text: "Switch to **Reel** and the editor changes: your video appears in a nine-by-sixteen frame so you can see exactly what gets cropped, and the live preview shows it the way Facebook will — full-bleed, with the reactions rail down the side.",
      },
      {
        type: "image",
        src: "/help/facebook-reels-and-stories/02-reel-selected.webp",
        alt: "The composer with Reel selected, showing a vertical video preview and Tag Collaborators option.",
        caption:
          "Reel format gives you a 9:16 preview and collaborator tagging.",
      },
      {
        type: "note",
        tone: "info",
        title: "Facebook allows exactly one collaborator",
        text: "Under More options you can invite another Page to collaborate on the Reel. That's Facebook's limit, not PostDaily's — and the invite is most reliable when you use the Page's numeric ID rather than its name.",
      },
      {
        type: "p",
        text: "Reels have no separate title field. Facebook shows your post text as the Reel's caption, so write the first line to work on its own.",
      },
      { type: "h2", text: "Stories" },
      {
        type: "p",
        text: "Stories work the same way: choose **Story**, attach a single vertical photo or video, and publish. Because they expire after 24 hours, they're a poor fit for anything evergreen.",
      },
      {
        type: "image",
        src: "/help/facebook-reels-and-stories/03-story-selected.webp",
        alt: "The composer with Story selected and a vertical video attached.",
      },
      {
        type: "note",
        tone: "info",
        title: "Stories can't take a first comment",
        text: "Facebook Stories don't accept comments at all, so the first-comment block doesn't apply when Story is selected.",
      },
      { type: "h2", text: "Video that Facebook will accept" },
      {
        type: "note",
        tone: "info",
        title: "H.264 is the safest choice",
        text: "Facebook transcodes whatever you upload, and less common codecs generally still work. H.264 remains the safest export setting because it needs no conversion — but if PostDaily flags a codec, that's a heads-up rather than a blocker, and you can publish anyway.",
      },
      {
        type: "ul",
        items: [
          "**Video** — MP4 or MOV, up to 4 GB.",
          "**Photos** — JPEG, PNG, GIF or WebP, up to 10 MB each.",
          "**Vertical media** — 9:16 is what Reels and Stories are designed for. Anything else gets cropped.",
        ],
      },
    ],
    related: ["schedule-facebook-reels", "schedule-facebook-stories"],
  },
  {
    slug: "schedule-facebook-reels",
    links: [
      { to: "/facebook-scheduler", label: "Facebook post scheduler" },
      {
        to: "/tools/video-checker",
        label: "Check a video before you upload it",
      },
      {
        blog: "schedule-facebook-posts-without-business-suite",
        label: "Schedule Facebook posts without Business Suite",
      },
    ],
    title: "How to schedule Facebook Reels",
    excerpt:
      "Yes, you can schedule Reels on a Facebook Page, in Meta Business Suite or in PostDaily. What each needs, the video rules, and what a scheduled Reel can't carry.",
    category: "Scheduling & queue",
    updated: "2026-09-27",
    read: "4 min",
    body: [
      {
        type: "p",
        text: "**Yes, you can schedule Facebook Reels — on a Page, not on a personal profile.** Facebook only lets Pages publish through its scheduling tools and its API, so both Meta Business Suite and PostDaily need a Page you manage. You need full control of the Page or content permission on it.",
      },
      { type: "h2", text: "In Meta Business Suite (free)" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Open Meta Business Suite and click Create reel",
            text: "It's on the home page and on the Content tab. On mobile, tap **+** and choose **Reel**.",
          },
          {
            title: "Choose the Page and add your video",
            text: "Under **Post to**, pick the Page. Add the video, pick a thumbnail and write the caption.",
          },
          {
            title: "Pick Schedule, then a date and time",
            text: "After the editing step, choose to schedule instead of sharing now, set the time and click **Schedule**.",
          },
        ],
      },
      {
        type: "p",
        text: "Scheduled Reels appear under **Content → Posts and reels → Scheduled**, where you can reschedule or move them to drafts.",
      },
      { type: "h2", text: "In PostDaily" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Attach one video and choose Reel",
            text: "The format control sits above the editor. Reel is disabled while an image is attached, because a Reel is one video only.",
          },
          {
            title: "Write the caption",
            text: "Your post text becomes the Reel's caption. There is no separate title field, so make the first line work on its own.",
          },
          {
            title: "Schedule it or add it to the queue",
            text: "Pick a time, or let the queue put it in the next open slot for that Page.",
          },
        ],
      },
      { type: "h2", text: "Video rules" },
      {
        type: "ul",
        items: [
          "**Length:** 3 to 90 seconds. PostDaily checks this before scheduling, so a Reel that Facebook would refuse never waits in the queue.",
          "**File:** MP4 or MOV, up to 4 GB. Vertical 9:16 is what Reels are designed for; other shapes get cropped.",
          "**Codec:** H.264 is the safest export. Facebook transcodes on upload, so other codecs usually work too. [Check a file before you upload it](/tools/video-checker).",
        ],
      },
      { type: "h2", text: "What a scheduled Reel can't carry" },
      {
        type: "ul",
        items: [
          "**No first comment.** PostDaily adds first comments to Facebook feed posts only. Put the link in the caption, or comment by hand once the Reel is live.",
          "**One collaborator.** You can invite one other Page to collaborate, which is Facebook's limit. Use the Page's numeric ID for the most reliable invite.",
          "**No music picker.** Meta's publishing API has no way to add a track from Facebook's music library, so put the sound in the video file itself.",
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "Do scheduled Reels get less reach?",
        text: "Meta hasn't said that scheduled Reels are ranked any differently, and its own Business Suite schedules them. What moves reach is the Reel itself: the first seconds, the length, and whether people watch to the end.",
      },
    ],
    related: ["facebook-reels-and-stories", "schedule-facebook-stories"],
  },
  {
    slug: "schedule-facebook-stories",
    links: [
      { to: "/facebook-scheduler", label: "Facebook post scheduler" },
      {
        to: "/tools/video-checker",
        label: "Check a video before you upload it",
      },
    ],
    title: "How to schedule Facebook Stories",
    excerpt:
      "Schedule a Facebook Page Story in Meta Business Suite or PostDaily, what survives scheduling and what doesn't, and posting the same Story to Instagram.",
    category: "Scheduling & queue",
    updated: "2026-09-27",
    read: "3 min",
    body: [
      {
        type: "p",
        text: "**You can schedule Stories on a Facebook Page, but not on a personal profile.** Meta Business Suite does it for free, and PostDaily does it alongside your other networks. Either way you need full control of the Page or content permission on it.",
      },
      { type: "h2", text: "In Meta Business Suite" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Click Create story",
            text: "From the home page or the Content tab. Under **Post to**, choose the Page, an Instagram account, or both.",
          },
          {
            title: "Add photos or videos, then edit",
            text: "Business Suite takes up to ten items. Its editor adds text, mentions, stickers and a link.",
          },
          {
            title: "Choose Schedule and set the time",
            text: "Scheduled Stories are under **Content → Stories → Scheduled**.",
          },
        ],
      },
      { type: "h2", text: "In PostDaily" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Attach one photo or video and choose Story",
            text: "A Facebook Story publishes one item at a time. A video Story must run 3 to 60 seconds.",
          },
          {
            title: "Schedule it, or add it to the queue",
            text: "The Story goes live at that time and expires 24 hours later, as every Story does.",
          },
        ],
      },
      { type: "h2", text: "What doesn't survive scheduling through an app" },
      {
        type: "ul",
        items: [
          "**No caption, stickers or link.** Meta's API publishes the photo or video only. If a Story needs a poll, a link sticker or music, publish it from the Facebook app or Business Suite instead.",
          "**No comments.** Stories don't take comments at all, so there is no first comment to schedule.",
          "**Collaborators on video only.** A collaborating Page can be invited on a video Story, not on a photo Story.",
        ],
      },
      { type: "h2", text: "The same Story on Instagram" },
      {
        type: "p",
        text: "Select your Facebook Page and your Instagram account in the same post and choose **Story** for each. Each network gets its own copy at the same time. Instagram also lets several images go out as consecutive Story frames; Facebook takes one.",
      },
    ],
    related: ["schedule-facebook-reels", "facebook-reels-and-stories"],
  },
  {
    slug: "facebook-scheduling-and-queue",
    links: [
      {
        blog: "how-to-schedule-facebook-posts",
        label: "How to schedule Facebook posts, Reels and Stories",
      },
      {
        blog: "schedule-facebook-group-posts",
        label: "Can you schedule posts in a Facebook Group?",
      },
      {
        blog: "schedule-facebook-posts-without-business-suite",
        label: "Schedule Facebook Page posts without Meta Business Suite",
      },
      {
        blog: "social-media-content-calendar",
        label: "A content calendar that survives a real week",
      },
    ],
    title: "Set your Facebook queue's posting times",
    excerpt:
      "Set your Facebook Page's posting times once and let new posts fill the next open slot, then reorder, skip, pause or reschedule the queue as needed.",
    category: "Scheduling & queue",
    updated: "2026-08-14",
    read: "5 min",
    video: guideVideo(
      "facebook",
      "facebook-scheduling-and-queue",
      "Setting a posting schedule and working the queue.",
    ),
    body: [
      {
        type: "p",
        text: "Scheduling in PostDaily runs on a **queue**. Instead of picking a time for every single post, you set your posting times once per channel — then new posts drop into the next open slot automatically.",
      },
      { type: "h2", text: "Set your posting times" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Open your channel and switch to Posting schedule",
            text: "From Channels, open your Facebook Page, then choose the Posting schedule tab.",
          },
        ],
      },
      {
        type: "image",
        src: "/help/facebook-scheduling-and-queue/01-posting-schedule-tab.webp",
        alt: "The channel settings tabs with Posting schedule highlighted.",
      },
      {
        type: "p",
        text: "This is where your week lives. Set a **weekly goal**, then fill it however you like — the progress bar tracks how close your current times get you.",
      },
      {
        type: "image",
        src: "/help/facebook-scheduling-and-queue/02-weekly-grid.webp",
        alt: "The posting schedule editor showing weekly cadence, quick presets and per-day time chips.",
        caption: "Every day can be toggled on or off without losing its times.",
      },
      {
        type: "ul",
        items: [
          "**Quick presets** fill a whole week in one click — weekdays at 9 AM, three times daily, and so on.",
          "**Auto-fill to goal** adds the times needed to reach your weekly goal.",
          "**Suggested times** are personalised from your channel's own recent engagement — tap one to add it.",
          "**Toggle a day off** to pause it without deleting its times.",
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "Times follow your channel's time zone",
        text: "The time zone is set on this page, so a schedule keeps making sense even when you're travelling. Each channel can have its own — your Facebook Page doesn't have to post at the same times as your other networks.",
      },
      { type: "h2", text: "How the queue fills" },
      {
        type: "p",
        text: "Once your times are saved, they become slots. The Queue page shows what's lined up and how many open slots are left.",
      },
      {
        type: "image",
        src: "/help/facebook-scheduling-and-queue/03-queue-timeline.webp",
        alt: "The queue page showing scheduled posts and the number of posts queued.",
      },
      {
        type: "ul",
        items: [
          "**Drag to reorder** — the rest of the queue reflows around what you moved.",
          "**Skip this time slot** pushes a post to the next available slot.",
          "**Remove from queue** sends it back to Drafts. Your work isn't deleted.",
        ],
      },
      { type: "h2", text: "Pause everything" },
      {
        type: "p",
        text: "Going away, or something happened in the news you'd rather not post through? Pause holds every post on the channel until you resume — nothing is lost, it just waits.",
      },
      {
        type: "image",
        src: "/help/facebook-scheduling-and-queue/04-pause-queue.webp",
        alt: "The queue toolbar with the Pause button highlighted.",
      },
      { type: "h2", text: "Prefer a calendar?" },
      {
        type: "p",
        text: "The Calendar view shows the same posts by day and time. Drag a post to a different day or slot to reschedule it — no need to reopen the composer.",
      },
    ],
    related: ["publish-your-first-facebook-post", "facebook-analytics"],
  },
  {
    slug: "facebook-first-comment",
    links: [
      { to: "/facebook-scheduler", label: "Facebook post scheduler" },
      { help: "publish-to-instagram", label: "Publish to Instagram" },
      { help: "publish-to-youtube", label: "Publish to YouTube" },
    ],
    title: "Put your link in the first comment",
    excerpt:
      "Facebook, Instagram and YouTube: publish a post and its first comments together, automatically — which networks take one, and the permission that trips it up.",
    category: "Composer & AI",
    updated: "2026-09-27",
    read: "4 min",
    video: guideVideo(
      "facebook",
      "facebook-first-comment",
      "Putting the link in a first comment instead of the caption.",
    ),
    body: [
      {
        type: "p",
        text: "A lot of Pages put their link in the **first comment** instead of the post. The usual reason is reach: many social managers find that posts with a link in the text travel less far, because the network would rather keep people on it. Meta doesn't publish how it ranks links, so treat that as a widely held observation rather than a rule, and test it on your own Page.",
      },
      {
        type: "p",
        text: "Doing it by hand means publishing the post, then commenting on it as the Page straight away. It works, but only if you are there the moment the post goes live.",
      },
      {
        type: "p",
        text: "PostDaily does that for you. You write the comment when you write the post, and it publishes the instant the post goes live — from the same Page, with no reminder to set and nothing to do on your phone.",
      },
      { type: "h2", text: "Add a first comment" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Write your post, then click Add comment",
            text: "It sits just below the editor, on channels that support first comments.",
          },
        ],
      },
      {
        type: "image",
        src: "/help/facebook-first-comment/01-add-comment.webp",
        alt: "The PostDaily composer with the Add comment button highlighted below the post editor.",
      },
      {
        type: "steps",
        start: 2,
        items: [
          {
            title: "Write the comment",
            text: "Put your link here, with any context you want alongside it. Facebook allows up to 8,000 characters, so there's plenty of room.",
          },
        ],
      },
      {
        type: "image",
        src: "/help/facebook-first-comment/02-comment-field.webp",
        alt: "The composer with the first comment field filled in and highlighted.",
        caption:
          "The comment is saved with the post and published straight after it.",
      },
      {
        type: "p",
        text: "That's all there is to it. Schedule or queue the post as normal — when it publishes, the comment follows immediately.",
      },
      { type: "h2", text: "More than one comment" },
      {
        type: "p",
        text: "Click **Add comment** again to add up to 5. They publish in order, each as its own comment on the post.",
      },
      { type: "h2", text: "Which networks take a first comment" },
      {
        type: "ul",
        items: [
          "**Facebook** — feed posts only, not Reels or Stories.",
          "**Instagram** — posts, carousels and Reels, not Stories.",
          "**YouTube** — videos and Shorts, except private videos, which YouTube doesn't let anyone comment on.",
          "**Threads, X, Bluesky and Mastodon** — use **Add to thread** instead: the follow-up publishes as a reply to your post, which is how those networks do it.",
          "**TikTok and Pinterest** — no. PostDaily doesn't post comments on either.",
        ],
      },
      { type: "h2", text: "If the comment doesn't appear" },
      {
        type: "note",
        tone: "warning",
        title: "First comments need the Pages engagement permission",
        text: "PostDaily asks for this when you connect a Page. If you connected before it was requested, the post will publish but the comment won't. Reconnect the channel once and it works from then on.",
      },
      {
        type: "ul",
        items: [
          "**Mentions don't work in comments.** Facebook only resolves @ mentions in the post caption itself.",
          "**Stories can't take comments.** If the post format is Story, the first-comment block doesn't apply.",
        ],
      },
    ],
    related: ["publish-your-first-facebook-post", "connect-a-facebook-page"],
  },
  {
    slug: "when-a-post-fails",
    links: [
      { to: "/tiktok-scheduler", label: "TikTok scheduler" },
      { to: "/linkedin-scheduler", label: "LinkedIn scheduler" },
      { to: "/pinterest-scheduler", label: "Pinterest scheduler" },
      {
        blog: "why-scheduled-posts-fail",
        label: "Why scheduled posts fail, and what to do about each cause",
      },
    ],
    title: "When a post fails",
    excerpt:
      "When a scheduled post fails: find it, read the network's own error rather than a generic one, and retry safely without posting it twice.",
    category: "Troubleshooting",
    updated: "2026-08-21",
    read: "3 min",
    video: guideVideo(
      "facebook",
      "when-a-post-fails",
      "Finding a failed post, reading the network's own error, and retrying.",
    ),
    body: [
      {
        type: "p",
        text: "Sometimes a post doesn't go out. PostDaily shows you the **actual message the network returned** rather than a generic failure — which is usually enough to fix it in under a minute.",
      },
      { type: "h2", text: "Find failed posts" },
      {
        type: "p",
        text: "Open the **Missed** tab in List view. Anything that didn't publish is collected here, newest first, with a red dot on the tab when something needs attention.",
      },
      {
        type: "image",
        src: "/help/when-a-post-fails/01-missed-tab.webp",
        alt: "The post list with the Missed tab highlighted.",
      },
      { type: "h2", text: "Read the reason, then retry" },
      {
        type: "p",
        text: "Every failed post carries the network's own error and a **Retry** button. The wording comes straight from the network, so it tells you what actually went wrong.",
      },
      {
        type: "image",
        src: "/help/when-a-post-fails/02-error-and-retry.webp",
        alt: "Failed posts showing provider error messages with Retry buttons highlighted.",
        caption: "The error text is exactly what the network reported.",
      },
      {
        type: "ul",
        items: [
          "**“Invalid link attachment”** — the link couldn't be fetched. Check it opens publicly, then retry.",
          "**“User not visible”** or **“cannot be tagged”** — a tagged account is private, or doesn't allow tagging. Remove the tag and retry.",
          "**A rejected video format** — re-export as MP4 with H.264 and retry.",
          "**Rate limiting** — nothing is wrong with your post. Retry, and it goes out.",
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "Retry never double-posts",
        text: "Before republishing, PostDaily checks whether the post actually made it out despite the error. If it did, the retry adopts that result instead of posting again — so a retry can't leave you with duplicates.",
      },
      { type: "h2", text: "When the channel is the problem" },
      {
        type: "p",
        text: "If a channel's connection has expired, posts fail until it's reconnected. The channel shows as needing attention in Channels, and reconnecting it fixes every post waiting behind it.",
      },
      {
        type: "note",
        tone: "info",
        title: "Connections are watched in the background",
        text: "PostDaily refreshes access tokens before they expire, so an expired channel should be unusual rather than routine.",
      },
    ],
    related: ["facebook-scheduling-and-queue", "connect-a-facebook-page"],
  },
  {
    slug: "facebook-analytics",
    title: "Facebook analytics in PostDaily",
    excerpt:
      "Facebook Page impressions, reach and video views from Meta's insights in PostDaily, and an honest account of what Facebook doesn't report to apps.",
    category: "Analytics & reporting",
    updated: "2026-08-14",
    read: "3 min",
    video: guideVideo(
      "facebook",
      "facebook-analytics",
      "Reading the analytics page, and what each network reports.",
    ),
    body: [
      {
        type: "p",
        text: "Analytics pulls your Facebook numbers straight from Meta's insights and stores a daily snapshot, so you can see how a post performed **over time** rather than only right now.",
      },
      { type: "h2", text: "Reading your numbers" },
      {
        type: "image",
        src: "/help/facebook-analytics/01-analytics-overview.webp",
        alt: "The analytics overview showing engagement metrics across channels.",
      },
      {
        type: "ul",
        items: [
          "**Impressions** — reflecting Meta's newer views-based reporting.",
          "**Reach** — unique accounts that saw the post.",
          "**Video views** — for video posts and Reels.",
          "**Reactions, comments and shares.**",
        ],
      },
      {
        type: "p",
        text: "Use the date range to compare periods, or switch to the Posts and Audience tabs for a per-post breakdown and follower growth.",
      },
      {
        type: "image",
        src: "/help/facebook-analytics/02-date-range.webp",
        alt: "The analytics date range selector with 30 days highlighted.",
      },
      { type: "h2", text: "What Facebook doesn't report" },
      {
        type: "p",
        text: "Not every network exposes every metric. Rather than showing a zero that reads like bad performance, PostDaily tells you when a number simply isn't available — and the availability button explains exactly what this network reports.",
      },
      {
        type: "image",
        src: "/help/facebook-analytics/03-metric-availability.webp",
        alt: "The metric availability button in the analytics toolbar, highlighted.",
      },
      {
        type: "note",
        tone: "info",
        title: "Facebook has no save metric",
        text: "Saves are an Instagram and Pinterest concept. Facebook's API doesn't expose them for Page posts, so PostDaily marks them unavailable instead of reporting zero.",
      },
      {
        type: "note",
        tone: "info",
        title: "Pinterest metrics are read live",
        text: "Pinterest's developer terms only let apps store campaign analytics, so PostDaily never saves pin metrics. A pin's impressions and saves are read from Pinterest when you open that pin in Post details, which means Pinterest has no history to chart on this page.",
      },
      { type: "h2", text: "Exporting" },
      {
        type: "p",
        text: "Export sends the current view to CSV — useful for a monthly report or your own spreadsheet.",
      },
      {
        type: "image",
        src: "/help/facebook-analytics/04-export.webp",
        alt: "The analytics Export button, highlighted.",
      },
      {
        type: "note",
        tone: "info",
        title: "Export is a paid-plan feature",
        text: "On the free plan the button explains that export isn't included. Everything else on this page works on every plan.",
      },
    ],
    related: [
      "facebook-scheduling-and-queue",
      "publish-your-first-facebook-post",
    ],
  },
];

/**
 * Product-wide guides that aren't tied to one social network. Kept in their own
 * array so the Facebook set stays easy to scan while more networks are added.
 */
const PRODUCT_ARTICLES: HelpArticle[] = [
  {
    slug: "plan-with-the-calendar",
    links: [
      {
        blog: "social-media-content-calendar",
        label: "A social media content calendar that survives a real week",
      },
    ],
    title: "Plan your week on the calendar",
    excerpt:
      "See everything scheduled across your channels on one calendar, switch between day, week and month views, and drag a post to a new time to move it.",
    category: "Scheduling & queue",
    updated: "2026-08-22",
    read: "3 min",
    video: guideVideo(
      "product",
      "plan-with-the-calendar",
      "Day, week and month views, the list, and filtering by channel or tag.",
    ),
    body: [
      {
        type: "p",
        text: "The calendar shows every post across every channel in one grid. It's the fastest way to spot a gap in the week, or three posts stacked on the same afternoon.",
      },
      { type: "h2", text: "Day, week and month" },
      {
        type: "p",
        text: "Switch the range in the top right. **Week** is the everyday view; **month** is for spotting the shape of a campaign; **day** is for a busy publishing day where posts would otherwise overlap.",
      },
      {
        type: "image",
        src: "/help/plan-with-the-calendar/01-calendar.webp",
        alt: "The calendar with the Day, Week and Month range controls highlighted.",
      },
      {
        type: "note",
        tone: "info",
        title: "Drag a post to reschedule it",
        text: "Drag any post to a different day or time slot and it reschedules on the spot — no need to open the composer. Posts already published can't be moved.",
      },
      { type: "h2", text: "Calendar or list" },
      {
        type: "p",
        text: "Some things are easier as a list — searching text, scanning failures, or bulk-selecting. Toggle between the two without losing your filters.",
      },
      {
        type: "image",
        src: "/help/plan-with-the-calendar/02-list-toggle.webp",
        alt: "The calendar with the List toggle highlighted.",
      },
      {
        type: "note",
        tone: "info",
        title: "Drafts only appear in List",
        text: "A draft has no scheduled time, so there's nowhere to put it on a calendar. Switch to List to see drafts alongside everything else.",
      },
      { type: "h2", text: "Filtering" },
      {
        type: "p",
        text: "Filter by tag or by channel to narrow what you're looking at. The status tabs — Scheduled, Drafts, Sent, Missed — work alongside the filters rather than replacing them.",
      },
      {
        type: "image",
        src: "/help/plan-with-the-calendar/03-filters.webp",
        alt: "The calendar toolbar with the Tags and Channels filters highlighted.",
      },
    ],
    related: ["facebook-scheduling-and-queue", "bulk-upload-csv"],
  },
  {
    slug: "bulk-upload-csv",
    links: [
      {
        to: "/tools/bulk-csv-checker",
        label: "Check your CSV before you upload it",
      },
      {
        to: "/tools/content-calendar-2027",
        label: "2027 content calendar, ready to import",
      },
      { to: "/pinterest-scheduler", label: "Pinterest scheduler" },
      {
        blog: "moving-from-buffer",
        label: "Moving from Buffer without losing your queue",
      },
      {
        blog: "social-media-content-calendar",
        label: "Plan a month of content",
      },
    ],
    title: "Bulk schedule social posts from a CSV",
    excerpt:
      "Upload a CSV spreadsheet to fill a channel's schedule in one go, with text, image links, tags and posting times, and fix any row the import rejects.",
    category: "Scheduling & queue",
    updated: "2026-09-27",
    read: "4 min",
    video: guideVideo(
      "product",
      "bulk-upload-csv",
      "Importing a month of posts from a spreadsheet, one channel at a time.",
    ),
    body: [
      {
        type: "p",
        text: "Planning a month in a spreadsheet is often faster than writing posts one at a time. Bulk upload takes that spreadsheet and turns each row into a scheduled post.",
      },
      { type: "h2", text: "Open bulk upload" },
      {
        type: "p",
        text: "It lives on the channel you're uploading to — open **Channels**, pick the channel, and find **Bulk Upload** on its settings page.",
      },
      {
        type: "image",
        src: "/help/bulk-upload-csv/01-bulk-upload-button.webp",
        alt: "The channel settings page with the Bulk Upload button highlighted.",
      },
      { type: "h2", text: "Start from the template" },
      {
        type: "p",
        text: "Download the template so the columns line up. Filling in your own spreadsheet from scratch is the most common reason an import fails.",
      },
      {
        type: "image",
        src: "/help/bulk-upload-csv/02-download-template.webp",
        alt: "The bulk upload dialog with the Download template button highlighted.",
      },
      { type: "h2", text: "What each column does" },
      {
        type: "ul",
        items: [
          "**text** — required, unless the row has an image. Up to 10,000 characters.",
          "**image_url** — optional. A direct, public `https` link to a JPG, PNG or WebP under 5 MB.",
          "**tags** — optional, comma-separated. Tags must **already exist** in this workspace, and they're case-sensitive.",
          "**posting_time** — `YYYY-MM-DD HH:MM` in 24-hour time. Leave it blank and the post joins the channel's queue instead.",
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "Times use the timezone you pick in the dialog",
        text: "Set it before importing. It defaults to your workspace timezone, which is usually what you want — but it's worth a glance if you're scheduling for an audience elsewhere.",
      },
      { type: "h2", text: "Uploading to a Pinterest channel" },
      {
        type: "p",
        text: "A pin needs a board, and a spreadsheet row has nowhere to name one. On a Pinterest channel the dialog asks you to **choose a board** before importing, and every pin in the file goes to it. To spread pins across boards, split the file and import each part with its own board.",
      },
      {
        type: "p",
        text: "Every row also needs an **image_url**, because Pinterest has no text-only pins. Rows without one are flagged before you import. A row's text becomes the pin's description, which Pinterest limits to 800 characters.",
      },
      { type: "h2", text: "Upload and import" },
      {
        type: "p",
        text: "Drag the CSV in, choose the file, or paste rows straight into the box. Then **Import posts**.",
      },
      {
        type: "image",
        src: "/help/bulk-upload-csv/03-import.webp",
        alt: "The bulk upload dialog with the Import posts button highlighted.",
      },
      {
        type: "note",
        tone: "warning",
        title: "Images are fetched when you import",
        text: "Each image_url is downloaded at import time, so the link has to be publicly reachable — a Google Drive or Dropbox share page usually isn't. If an image can't be fetched, that row tells you rather than failing silently later.",
      },
      {
        type: "note",
        tone: "info",
        title: "A row that can't be scheduled is saved to drafts",
        text: "If a row breaks one of the network's rules, such as text over its character limit, the post is still created and kept in your drafts, and the import summary says what to fix. Fix it there: importing the file again would create a second copy of every row.",
      },
    ],
    related: ["plan-with-the-calendar", "media-library"],
  },
  {
    slug: "write-with-ai",
    links: [
      {
        help: "connect-ai-apps",
        label: "Use PostDaily from Claude, ChatGPT and other AI apps",
      },
    ],
    title: "Use the AI Assistant",
    excerpt:
      "Write from a brief, rewrite with one tap, tailor each channel and schedule at your best time — nothing changes until you say so.",
    category: "Composer & AI",
    updated: "2026-09-25",
    read: "5 min",
    video: guideVideo(
      "product",
      "assistant-walkthrough",
      "A step-by-step tour of the Assistant, from a brief to the Schedule card.",
    ),
    body: [
      {
        type: "p",
        text: "The Assistant lives in the composer, in the **Assistant** tab beside Preview. It works on the post you're writing — from a blank draft to a scheduled post — and nothing changes until you say so.",
      },
      {
        type: "image",
        src: "/help/write-with-ai/01-assistant-tab.webp",
        alt: "The composer with the Assistant tab open and its empty state, What are we posting?",
      },
      { type: "h2", text: "Write from a brief" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Pick your channels",
            text: "Select them under Publishing to first. The Assistant sizes every option for the channels you picked.",
          },
          {
            title: "Open the Assistant tab",
            text: "It sits beside Preview. The **+** button starts a new chat.",
          },
          {
            title: "Describe the post",
            text: "Say what it's about, who it's for and any dates. Or pick a starter: **Write a post about…**, **Give me ideas for this week** or **When should I post?**",
          },
        ],
      },
      {
        type: "image",
        src: "/help/write-with-ai/02-three-options.webp",
        alt: "Three options from the Assistant, each with a character count per channel and an Apply button.",
      },
      {
        type: "steps",
        start: 4,
        items: [
          {
            title: "Pick one",
            text: "Each option shows its character count for every channel you picked, such as 216/300 for Bluesky. **Apply** puts it in your draft; **+** adds it to the end of the draft instead.",
          },
        ],
      },
      {
        type: "image",
        src: "/help/write-with-ai/03-applied-to-draft.webp",
        alt: "The chosen option applied to the draft in the composer's editor.",
      },
      { type: "h2", text: "Quick rewrites" },
      {
        type: "p",
        text: "Once the draft has text, one-tap chips appear under the chat: **Rephrase**, **Shorten**, **Expand**, **Fix grammar**, **More engaging**, **Add hashtags**, **Add emojis** and more. **Tailor per channel** appears when you've picked more than one channel, **Split into thread** when a channel supports threads, and **Translate** offers eight languages.",
      },
      {
        type: "image",
        src: "/help/write-with-ai/04-rewrite-chips.webp",
        alt: "The row of rewrite chips under the chat: Rephrase, Shorten, Expand, Fix grammar.",
      },
      {
        type: "note",
        tone: "info",
        title: "A chip applies straight away",
        text: "Tapping a chip counts as your go-ahead, so its change lands in the draft right away with an **Undo** button beside it. Undo reverts one step. Rephrase and More engaging show options to pick from instead.",
      },
      {
        type: "image",
        src: "/help/write-with-ai/05-chip-undo.webp",
        alt: "The Assistant's reply after Add hashtags, with Updated the draft and the Undo button highlighted.",
      },
      { type: "h2", text: "Typed requests ask first" },
      {
        type: "p",
        text: "When you type a request that would replace text you've already written, the Assistant doesn't overwrite it. It shows a **Replace your draft?** card: **Replace draft** applies the new text, **Keep mine** leaves yours alone.",
      },
      { type: "h2", text: "Tone and brand voice" },
      {
        type: "p",
        text: "The **Tone** menu sets the tone for this chat: Professional, Friendly, Witty, Bold, Inspirational or Informative. The palette button beside it opens **AI & Brand voice** in a new tab.",
      },
      {
        type: "image",
        src: "/help/write-with-ai/06-tone-and-brand-voice.webp",
        alt: "The Tone menu and the Edit brand voice button under the Assistant's message box.",
      },
      {
        type: "p",
        text: "Brand voice applies to everyone in the workspace. Describe how you sound and what to avoid (up to 2,000 characters), set a default tone, choose how much emoji and how many hashtags you like, and add up to five example posts. Click **Save brand voice** to keep it. Owners, admins and editors can change it.",
      },
      {
        type: "image",
        src: "/help/write-with-ai/09-brand-voice-settings.webp",
        alt: "The AI & Brand voice settings page with the How this workspace sounds field highlighted.",
      },
      { type: "h2", text: "Ideas and top posts" },
      {
        type: "p",
        text: "Ask for ideas and you get five angles, each tagged with a format such as thread, carousel or short video. **Write this** turns one into a full post. If it can't tell what you post about yet — from your brand voice or the draft — it asks first, so naming your niche in brand voice gets you ideas straight away. On an empty draft, **Repurpose a top post** looks at your best posts from the last 30 days and gives one a fresh hook. It appears once the workspace has published something.",
      },
      {
        type: "image",
        src: "/help/write-with-ai/07-ideas.webp",
        alt: "Five post ideas from the Assistant, each with a format tag and a Write this link.",
      },
      { type: "h2", text: "Scheduling" },
      {
        type: "p",
        text: "Ask when to post and the Assistant uses this workspace's own engagement from the last 90 days. It can set up the schedule too, but nothing is scheduled until you press **Confirm**.",
      },
      { type: "h2", text: "Chat history and credits" },
      {
        type: "p",
        text: "Every chat is saved. The history button lists your recent chats — click a chat's title to rename it — and **+** starts a new one. Each message you send uses one AI credit, however much work the reply takes. The meter beside Send shows how many you've used this month.",
      },
      {
        type: "image",
        src: "/help/write-with-ai/08-history-and-credits.webp",
        alt: "The Chat history button and the AI credits meter in the Assistant panel.",
      },
      {
        type: "note",
        tone: "info",
        title: "How many credits you get",
        text: "Free includes 5 AI credits a month, Starter 50, Pro 500 and Agency 3,000. Credits reset on the 1st of each month. If you run out, the chat tells you and offers an upgrade.",
      },
      {
        type: "note",
        tone: "info",
        title: "Always read it before publishing",
        text: "The Assistant drafts; it doesn't check facts or whether a claim is true. Treat every result as a first draft, not a finished post.",
      },
    ],
    related: [
      "tailor-a-post-per-channel",
      "find-your-best-time-to-post",
      "templates-and-tags",
    ],
  },
  {
    slug: "tailor-a-post-per-channel",
    links: [
      {
        blog: "cross-post-bluesky-mastodon",
        label: "How to cross-post between Bluesky and Mastodon",
      },
      { blog: "what-is-the-fediverse", label: "What is the fediverse?" },
    ],
    title: "Tailor a post for each channel",
    excerpt:
      "Let the AI Assistant write a right-sized version of your post for every channel you picked, then review and approve each one before it lands.",
    category: "Composer & AI",
    updated: "2026-09-25",
    read: "2 min",
    body: [
      {
        type: "p",
        text: "Each network has its own limits and style. The Assistant can write a separate version for every channel you picked — a tight cut for Bluesky's 300 characters, a fuller one for Mastodon — while your shared text stays as it is.",
      },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Pick two or more channels",
            text: "Tailoring needs more than one channel under Publishing to.",
          },
          {
            title: "Write or apply a draft",
            text: "Type it yourself, or apply one of the Assistant's options.",
          },
          {
            title: "Ask for a version per channel",
            text: 'Type something like "Tailor it per channel: a tighter cut for Bluesky, a fuller one for Mastodon." Or tap the **Tailor per channel** chip.',
          },
        ],
      },
      {
        type: "image",
        src: "/help/tailor-a-post-per-channel/01-replace-your-draft.webp",
        alt: "A Replace your draft? card from the Assistant, with Replace draft and Keep mine highlighted.",
      },
      {
        type: "steps",
        start: 4,
        items: [
          {
            title: "Approve each version",
            text: "A typed request shows a **Replace your draft?** card for each channel. **Replace draft** applies it; **Keep mine** leaves that channel's text alone.",
          },
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "The chip doesn't ask",
        text: "Tapping **Tailor per channel** counts as your go-ahead, so both versions apply straight away, with **Undo** if you change your mind.",
      },
      { type: "h2", text: "Check each version" },
      {
        type: "p",
        text: "A pencil on a channel's tab means it has its own text. Open the tab to read or edit it. **Reset to shared text** puts that channel back on the shared draft.",
      },
      {
        type: "image",
        src: "/help/tailor-a-post-per-channel/02-channel-versions.webp",
        alt: "The Bluesky tab with its pencil mark, showing the customized Bluesky version in the editor.",
      },
    ],
    related: ["write-with-ai", "find-your-best-time-to-post"],
  },
  {
    slug: "find-your-best-time-to-post",
    links: [
      {
        blog: "best-time-to-post-on-bluesky",
        label: "We analysed 8,373 Bluesky posts",
      },
      {
        blog: "best-time-to-post-on-mastodon",
        label: "The best time to post on Mastodon",
      },
      {
        blog: "how-often-to-post-on-bluesky",
        label: "How often to post on Bluesky",
      },
      { to: "/tools/bluesky-best-time", label: "Bluesky best-time finder" },
      { to: "/tools/mastodon-best-time", label: "Mastodon best-time finder" },
    ],
    title: "Let the AI Assistant pick a posting time",
    excerpt:
      "Ask the Assistant when to post. It checks your own engagement from the last 90 days and can set up the schedule for you.",
    category: "Composer & AI",
    updated: "2026-09-25",
    read: "2 min",
    body: [
      {
        type: "p",
        text: "The Assistant can tell you when your posts do best and set up the schedule. It uses this workspace's own engagement over the last 90 days, not generic advice.",
      },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Open a post, then the Assistant tab",
            text: "Pick the channels you're posting to first.",
          },
          {
            title: "Ask when to post",
            text: 'For example, "When\'s my best time to post this tomorrow? Set it up." On a new chat you can also pick **When should I post?**',
          },
        ],
      },
      {
        type: "image",
        src: "/help/find-your-best-time-to-post/01-schedule-card.webp",
        alt: "The Assistant's Schedule this post? card, with the channels, the time, the reason and Confirm and Cancel.",
      },
      {
        type: "steps",
        start: 3,
        items: [
          {
            title: "Check the Schedule card",
            text: "It shows the channels and the time, with the reason underneath.",
          },
          {
            title: "Press Confirm to schedule it",
            text: "Or **Cancel** to leave things as they are. Nothing is scheduled until you press Confirm.",
          },
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "Not enough history yet?",
        text: "Where your own posts don't have enough data for a day, the Assistant says so and marks that suggestion as general advice.",
      },
      {
        type: "note",
        tone: "warning",
        title: "Fix the composer first",
        text: "If something needs fixing before the post can go out — no channel selected, for example — the card says so and Confirm stays unavailable until it's fixed.",
      },
    ],
    related: [
      "write-with-ai",
      "tailor-a-post-per-channel",
      "plan-with-the-calendar",
    ],
  },
  {
    slug: "templates-and-tags",
    title: "Reuse posts with templates and tags",
    excerpt:
      "Save a post you write often as a template to reuse in the composer, and use tags to keep months of scheduled and published posts findable.",
    category: "Composer & AI",
    updated: "2026-08-22",
    read: "2 min",
    video: guideVideo(
      "product",
      "templates-and-tags",
      "Saving a post you will write again, and grouping a campaign with tags.",
    ),
    body: [
      { type: "h2", text: "Templates" },
      {
        type: "p",
        text: "If you post the same shape of thing regularly — a weekly roundup, a product drop, a Friday question — save it once as a template and start from it next time.",
      },
      {
        type: "p",
        text: "Write the post, then open **Templates → Save current as template**. It's available in every future composer session.",
      },
      {
        type: "image",
        src: "/help/templates-and-tags/01-templates.webp",
        alt: "The composer's Templates menu with Save current as template highlighted.",
      },
      { type: "h2", text: "Tags" },
      {
        type: "p",
        text: "Tags label posts so you can find them later. They're workspace-wide, so a tag you create in the composer is immediately available when filtering the calendar or the post list.",
      },
      {
        type: "image",
        src: "/help/templates-and-tags/02-tags.webp",
        alt: "The composer's Tags menu with the search-or-create field highlighted.",
      },
      {
        type: "note",
        tone: "info",
        title: "Type to create one",
        text: "There's no separate screen for managing tags — typing a new name in this box creates it. Tags are case-sensitive, which matters if you also import posts by CSV: a tag there must match one that already exists, exactly.",
      },
    ],
    related: ["write-with-ai", "plan-with-the-calendar"],
  },
  {
    slug: "your-dashboard",
    links: [
      {
        blog: "bluesky-analytics-what-you-can-measure",
        label: "Bluesky analytics: what you can actually measure",
      },
      {
        to: "/tools/bluesky-engagement-rate",
        label: "Bluesky engagement rate calculator",
      },
    ],
    title: "Your dashboard at a glance",
    excerpt:
      "Your PostDaily dashboard shows what's publishing next, how the last 30 days went, and the two or three things worth doing today, in one screen.",
    category: "Getting started",
    updated: "2026-08-22",
    read: "3 min",
    video: guideVideo(
      "product",
      "your-dashboard",
      "What goes out next, the week ahead, and what is waiting on you.",
    ),
    body: [
      {
        type: "p",
        text: "The dashboard answers three questions: what goes out next, how recent posts performed, and what needs your attention. It's designed to be the page you check rather than the page you work in.",
      },
      { type: "h2", text: "Next up, and your week" },
      {
        type: "p",
        text: "**Next up** shows the post at the front of your queue with a live countdown, and lets you skip or edit it without leaving the page. **This week** tracks how many posts you've published against your streak.",
      },
      {
        type: "image",
        src: "/help/your-dashboard/01-next-up.webp",
        alt: "The dashboard showing the Next up card and the This week streak panel highlighted.",
      },
      { type: "h2", text: "Performance" },
      {
        type: "p",
        text: "Impressions, engagement rate, reach and new followers, with the change against the previous period. Switch between 7, 30 and 90 days.",
      },
      {
        type: "image",
        src: "/help/your-dashboard/02-kpis.webp",
        alt: "The dashboard KPI strip with Impressions and the 30 days range highlighted.",
      },
      {
        type: "note",
        tone: "info",
        title: "A zero is a real zero",
        text: "If a number is genuinely 0, you'll see 0. Where a network doesn't report a metric at all, PostDaily says so instead of showing a zero that reads like bad performance — see the analytics guide for which networks report what.",
      },
      { type: "h2", text: "Do next" },
      {
        type: "p",
        text: "The most useful part of the page. PostDaily surfaces what actually needs doing — posts that failed and can be retried, a channel that needs reconnecting, or a good time to post today — each with the action attached.",
      },
      {
        type: "image",
        src: "/help/your-dashboard/03-do-next.webp",
        alt: "The dashboard Do next panel highlighted, showing failed posts with a Retry action.",
      },
      {
        type: "p",
        text: "Further down, **Best time to post** maps engagement by day and hour from your own history, and **Performance by network** compares your channels over the same period.",
      },
    ],
    related: ["facebook-analytics", "plan-with-the-calendar"],
  },
  {
    slug: "notification-preferences",
    title: "Choose which notifications you get",
    excerpt:
      "Decide which PostDaily notifications reach you by email and which stay in the app, category by category rather than all or nothing.",
    category: "Account & security",
    updated: "2026-08-22",
    read: "2 min",
    video: guideVideo(
      "product",
      "notification-preferences",
      "The two emails worth keeping on, and the rest.",
    ),
    body: [
      {
        type: "p",
        text: "Notifications are set per category, each with separate **Email** and **In-app** switches. So you can be emailed the things that need action and leave the rest in the app.",
      },
      {
        type: "image",
        src: "/help/notification-preferences/01-preferences.webp",
        alt: "The notification preferences page with Publishing failures and Account reconnect highlighted.",
      },
      { type: "h2", text: "The categories" },
      {
        type: "ul",
        items: [
          "**Publishing failures** — posts that failed and need review. The one worth keeping on email.",
          "**Publishing success** — confirmations that a post went out.",
          "**Account reconnect** — a channel needs reauthorizing. Also worth keeping on email: until it's fixed, nothing on that channel publishes.",
          "**Client approvals** — decisions on review links, and posts waiting on approval.",
          "**Billing** — plan and payment issues that affect access.",
          "**Security** — account and sign-in updates.",
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "Weekly digest isn't sending yet",
        text: "The category is listed so the preference is ready, but digest delivery hasn't shipped — the page says as much. Turning it on now simply records your choice for when it does.",
      },
      { type: "h2", text: "Saving" },
      {
        type: "p",
        text: "Changes aren't live until you press **Save preferences**.",
      },
      {
        type: "image",
        src: "/help/notification-preferences/02-save.webp",
        alt: "The notification preferences page with the Save preferences button highlighted.",
      },
      {
        type: "note",
        tone: "warning",
        title: "Turning off reconnect emails is a real risk",
        text: "An expired channel stops publishing until someone reconnects it. If nothing emails you, the first sign is usually a week of posts that never went out.",
      },
    ],
    related: ["when-a-post-fails", "two-factor-authentication"],
  },
  {
    slug: "connect-instagram",
    links: [
      {
        to: "/instagram-scheduler",
        label: "Instagram scheduler: what you can post",
      },
    ],
    title: "Connect an Instagram account",
    excerpt:
      "Connect an Instagram business or creator account by Direct Login or through a Facebook Page, and the one difference that decides which you want.",
    category: "Connecting channels",
    updated: "2026-08-22",
    read: "3 min",
    video: guideVideo(
      "instagram",
      "connect-instagram",
      "Both ways to connect Instagram, and what each one unlocks.",
    ),
    body: [
      {
        type: "p",
        text: "Instagram needs a **Business or Creator** account. A personal profile can't be published to on a schedule by any tool — that's Instagram's rule, and converting is free in the Instagram app.",
      },
      {
        type: "p",
        text: "Open **Connect channel** and choose Instagram.",
      },
      {
        type: "image",
        src: "/help/connect-instagram/01-instagram-card.webp",
        alt: "The connect page with the Connect button on the Instagram card highlighted.",
      },
      { type: "h2", text: "Two ways in" },
      {
        type: "image",
        src: "/help/connect-instagram/02-two-methods.webp",
        alt: "The Instagram connect dialog showing Direct Instagram Login and Via Facebook Page.",
        caption: "The choice matters — see below before you pick.",
      },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Direct Instagram Login",
            text: "Sign in with your Instagram credentials. No Facebook Page needed. The simplest route if your account isn't linked to a Page.",
          },
          {
            title: "Via Facebook Page",
            text: "For a professional Instagram account linked to a Facebook Page you administer. You'll authorize through Facebook instead.",
          },
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "Which should you pick?",
        text: "**Via Facebook Page** unlocks one thing Direct Login cannot do: inviting **collaborators** to co-author a post. If that matters to you, connect through the Page. Otherwise Direct Login is fewer steps.",
      },
      {
        type: "p",
        text: "Once connected, the account appears in Channels and is ready to schedule.",
      },
      {
        type: "image",
        src: "/help/connect-instagram/03-connected.webp",
        alt: "The channels page listing the connected Instagram accounts.",
      },
      { type: "h2", text: "If it won't connect" },
      {
        type: "ul",
        items: [
          '**"Not a professional account"** — switch to Business or Creator in the Instagram app, then retry.',
          "**Connecting via Facebook, but the account isn't listed** — the Instagram account must be linked to a Page you administer. Link it in the Page's settings first.",
          "**You want both accounts** — connect them one at a time. Each becomes its own channel with its own schedule.",
        ],
      },
      {
        type: "note",
        tone: "warning",
        title: "Product tagging isn't available yet",
        text: "The Products tab appears on accounts connected through a Facebook Page, but PostDaily can't tag products yet. Instagram also requires an approved Shop catalog for it.",
      },
    ],
    related: ["connect-a-facebook-page", "publish-your-first-facebook-post"],
  },
  {
    slug: "connect-and-post-to-threads",
    links: [
      { to: "/threads-scheduler", label: "Threads scheduler" },
      {
        blog: "bluesky-vs-mastodon-vs-threads",
        label: "Bluesky vs Mastodon vs Threads",
      },
      {
        to: "/tools/thread-splitter",
        label: "Split a long post into a thread",
      },
    ],
    title: "Connect and post to Threads",
    excerpt:
      "Connect a Threads account with one button, then set the two options people look for when scheduling Threads posts: who can reply, and the topic tag.",
    category: "Connecting channels",
    updated: "2026-08-22",
    read: "3 min",
    video: guideVideo(
      "threads",
      "connect-and-post-to-threads",
      "Connecting Threads and what the composer gives you.",
    ),
    body: [
      {
        type: "p",
        text: "Threads runs on your Instagram identity — if you can post on Threads, you can connect it here. Open **Connect channel** and pick Threads.",
      },
      {
        type: "image",
        src: "/help/connect-and-post-to-threads/01-threads-card.webp",
        alt: "The connect page with the Connect button on the Threads card highlighted.",
      },
      {
        type: "p",
        text: "There's nothing to fill in. Threads takes you to its own authorization screen, you approve the publishing permission, and you land back here connected.",
      },
      {
        type: "image",
        src: "/help/connect-and-post-to-threads/02-authorize.webp",
        alt: "The Threads connect dialog with the Continue with Threads button highlighted.",
      },
      { type: "h2", text: "Writing the post" },
      {
        type: "p",
        text: "Threads allows **500 characters**, and the counter in the composer counts down so you can see the ceiling coming. Need more room? **Add to thread** turns one post into a chain — each segment gets its own text and its own media, and they publish in order as self-replies.",
      },
      { type: "h2", text: "Who can reply, and topic tags" },
      {
        type: "p",
        text: "Open **Advanced options** under the editor. Two Threads-specific settings live there.",
      },
      {
        type: "image",
        src: "/help/connect-and-post-to-threads/03-who-can-reply.webp",
        alt: "The composer's Advanced options showing the Who can reply choices and the Topic tag field.",
      },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Who can reply",
            text: "Everyone, profiles you follow, your followers, mentioned accounts only, or nobody but you. Set it before publishing — it's part of the post.",
          },
          {
            title: "Topic tag",
            text: "One tag, up to 50 characters, no periods or ampersands. It helps people find the post through the topic rather than through you.",
          },
        ],
      },
      { type: "h2", text: "Attaching a link" },
      {
        type: "p",
        text: "**Link attachment** gives a text post a proper link card. It's fussier than it looks, so the rules are worth knowing before you rely on it.",
      },
      {
        type: "image",
        src: "/help/connect-and-post-to-threads/04-link-attachment.webp",
        alt: "The Link attachment field in the composer's Advanced options.",
      },
      {
        type: "ul",
        items: [
          "**Text-only posts.** Attach a photo or video and the link attachment is ignored.",
          "**Public URLs with Open Graph metadata.** A page behind a login has nothing for Threads to read.",
          "**Not every destination works** — links to other Threads posts and some domains are rejected.",
          "**Five unique links per post**, counting any in the caption.",
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "Video is fine",
        text: "Threads re-encodes video on upload, so exports that other tools reject usually go through. If a video does fail, the error from Threads appears on the post — read that before re-exporting anything.",
      },
    ],
    related: [
      "connect-instagram",
      "connect-and-post-to-bluesky",
      "when-a-post-fails",
    ],
  },
  {
    slug: "connect-and-post-to-tiktok",
    links: [
      { to: "/tiktok-scheduler", label: "TikTok scheduler" },
      { limits: "tiktok", label: "TikTok video, caption and photo limits" },
      {
        to: "/tools/video-checker",
        label: "Check a video before you upload it",
      },
    ],
    title: "Connect and post to TikTok",
    excerpt:
      "Connect a TikTok account, then choose per post whether it publishes on its own or lands in your TikTok inbox to finish, and set the audience TikTok requires.",
    category: "Connecting channels",
    updated: "2026-09-27",
    read: "4 min",
    body: [
      {
        type: "p",
        text: "Open **Connect a channel**, pick TikTok, and approve PostDaily on TikTok's own screen. Any TikTok account can connect. You land back in PostDaily with the account listed as a channel.",
      },
      { type: "h2", text: "Post automatically, or get a reminder" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Post automatically",
            text: "Publishes the video or photo post to your profile at the scheduled time. Nothing to do on your phone.",
          },
          {
            title: "Get a reminder",
            text: "Sends it to your TikTok inbox as a draft instead. Open TikTok, choose the audience and publish within 24 hours. Only the caption carries over, so use this when you want to add a sound from TikTok's library, which apps can't attach.",
          },
        ],
      },
      { type: "h2", text: "The settings TikTok asks for" },
      {
        type: "ul",
        items: [
          "**Audience** — required, with no default: pick who can see the post from the choices your account offers (**Select an audience**). It is checked against your account's current options again when the post publishes.",
          "**Comment, Duet and Stitch** — each stays off until you turn it on. Duet and Stitch apply to videos only.",
          "**Your brand** and **Branded content** — mark a post that promotes your own business or a paid partnership. Branded content can't be set to only you, which is TikTok's rule.",
          "**AI-generated** — label content made with AI.",
          "**Cover** — a frame from the video, or for a photo post, the cover photo. Photo posts also offer **Auto-add music**.",
        ],
      },
      { type: "h2", text: "Videos and photo posts" },
      {
        type: "ul",
        items: [
          "**A video**: MP4, MOV or WebM, up to 4 GB through TikTok's API and within your plan's file-size cap.",
          "**A photo post**: several photos, up to your plan's media-per-post limit. The first 90 characters of the caption become its title. PNG photos are converted to JPEG for you.",
          "**Not both**: a post is either a video or photos, and TikTok has no text-only posts.",
          "**Captions** run to 2,200 characters, with a warning from 2,100.",
        ],
      },
      {
        type: "note",
        tone: "warning",
        title: "Video length isn't checked before publishing",
        text: "TikTok sets each account's maximum video length, and PostDaily doesn't check a video against it yet, so an over-long video can be refused when it publishes. Check yours in the TikTok app, and see the [TikTok limits](/limits/tiktok) for the rest.",
      },
      { type: "h2", text: "After it publishes" },
      {
        type: "p",
        text: "A post's public link appears once TikTok's moderation releases it, which can take a few hours. Videos report views, likes, comments and shares; photo posts return no metrics. If TikTok refuses a post, its own message is shown on the post, and posting-frequency errors are retried rather than failed.",
      },
    ],
    related: ["when-a-post-fails", "tailor-a-post-per-channel"],
  },
  {
    slug: "connect-and-post-to-pinterest",
    links: [
      { to: "/pinterest-scheduler", label: "Pinterest scheduler" },
      { limits: "pinterest", label: "Pinterest Pin size and limits" },
      { help: "bulk-upload-csv", label: "Bulk upload Pins from a CSV" },
    ],
    title: "Connect and post to Pinterest",
    excerpt:
      "Connect Pinterest, then schedule image, carousel and video Pins to a board or section, with a title, destination link and alt text on each.",
    category: "Connecting channels",
    updated: "2026-09-27",
    read: "3 min",
    body: [
      {
        type: "p",
        text: "Open **Connect a channel**, pick Pinterest, and approve PostDaily on Pinterest's own screen. A business account is best: publishing works with any account, but Pin analytics need a business account. Once connected, your boards, including secret ones, are listed in the composer.",
      },
      {
        type: "note",
        tone: "info",
        title: "Secret boards missing?",
        text: "Accounts connected before secret boards were supported need one reconnect to see them. Reconnect from the channel's settings.",
      },
      { type: "h2", text: "Every Pin needs a board" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Board",
            text: "Required. Pick it from your account's own boards.",
          },
          {
            title: "Section",
            text: "Optional. **Board root** posts to the board itself; or choose one of its sections.",
          },
          {
            title: "Title, Destination link and Alt text",
            text: "Title up to 100 characters, a link where the Pin leads, and alt text up to 500 characters. The post text becomes the description, up to 800 characters.",
          },
        ],
      },
      { type: "h2", text: "What a Pin can be" },
      {
        type: "ul",
        items: [
          "**An image Pin**: one image. 1000×1500 pixels, a 2:3 ratio, is the size Pinterest recommends.",
          "**A carousel**: 2 to 5 images.",
          "**A video Pin**: one video plus one image, which becomes the Pin's cover. Pinterest asks for a cover with every video Pin, so a video on its own can't be published.",
          "**No text-only Pins.** Pinterest needs an image or a video.",
        ],
      },
      { type: "h2", text: "Many Pins at once" },
      {
        type: "p",
        text: "Bulk upload takes a CSV of Pins for one board at a time: choose the board once for the whole file, and give every row an image URL. See [Bulk schedule social posts from a CSV](/help/bulk-upload-csv), and check the file first with the [free CSV checker](/tools/bulk-csv-checker).",
      },
      { type: "h2", text: "Pin analytics" },
      {
        type: "p",
        text: "Open a published Pin and PostDaily reads its impressions and saves over the last 30 days straight from Pinterest. They aren't stored, because Pinterest's developer terms only let apps keep campaign analytics, so there is no long-term history to chart.",
      },
    ],
    related: ["bulk-upload-csv", "when-a-post-fails"],
  },
  {
    slug: "connect-and-post-to-x",
    links: [
      { to: "/twitter-scheduler", label: "X (Twitter) scheduler" },
      {
        to: "/tools/thread-splitter",
        label: "Split a long post into a thread",
      },
      {
        help: "character-limits-by-network",
        label: "How each network counts characters",
      },
    ],
    title: "Connect and post to X (Twitter)",
    excerpt:
      "Connect X on a paid plan, then schedule posts, threads and polls with reply settings. How the monthly X budget works, and what uses it.",
    category: "Connecting channels",
    updated: "2026-09-27",
    read: "4 min",
    body: [
      {
        type: "p",
        text: "**X is on paid plans only.** X charges for every post made through its API, so the Free plan can't connect X, and PostDaily says so if you try. On a paid plan, open **Connect a channel**, pick X, and approve PostDaily on X's own screen.",
      },
      { type: "h2", text: "Posts, threads and polls" },
      {
        type: "ul",
        items: [
          "**A post**: 280 characters, counted the way X counts. Every link is 23 characters however long it is, and emoji and Chinese, Japanese or Korean characters count as 2. The composer's counter uses the same rules.",
          "**Media**: up to 4 images or one video. GIFs can't be uploaded.",
          "**A thread**: click **Add to thread** to write the next post. Each post in the chain gets its own text and media, up to 25, and they publish in order as replies.",
          "**A poll**: 2 to 4 choices of up to 25 characters, open from 5 minutes to 7 days. A poll can't carry media.",
          "**Who can reply**: everyone, accounts you follow, mentioned users, subscribers or verified accounts. Polls and reply settings apply to the first post of a thread.",
        ],
      },
      { type: "h2", text: "The monthly X budget" },
      {
        type: "p",
        text: X_BUDGET_TEXT,
      },
      {
        type: "ul",
        items: [
          "**A link costs more.** X charges about 13 times as much for a post containing a link, including one written without https://.",
          "**Threads count per post.** A five-post thread uses five posts' worth.",
          "**Reading metrics uses a little.** Each check of a post's likes and impressions costs a small amount, so a post uses some budget after it publishes too.",
          "**Your billing page shows what is left**, as an estimate of the posts it still covers.",
        ],
      },
      {
        type: "note",
        tone: "warning",
        title: "When the budget runs out",
        text: "X posts fail with a message saying so, and aren't retried automatically, so nothing spends past your budget. It resets at the start of each month; retry them then, or upgrade for a larger budget.",
      },
      { type: "h2", text: "Other things X refuses" },
      {
        type: "ul",
        items: [
          "**Duplicate text.** X rejects a post that repeats one you posted recently. Change the wording.",
          "**Long posts.** There are no long posts through the API: 280 is the limit, so split longer ideas into a thread.",
        ],
      },
    ],
    related: ["character-limits-by-network", "manage-your-plan"],
  },
  {
    slug: "connect-and-post-to-bluesky",
    links: [
      {
        blog: "how-to-schedule-bluesky-posts",
        label: "How to schedule Bluesky posts",
      },
      {
        blog: "bluesky-app-passwords-explained",
        label: "Bluesky app passwords, explained",
      },
      {
        to: "/tools/bluesky-best-time",
        label: "Find your best time to post on Bluesky",
      },
    ],
    title: "Connect Bluesky with an app password",
    excerpt:
      "Connect Bluesky with an app password, never your account password, then set content labels and languages and write within the 300-character limit.",
    category: "Connecting channels",
    updated: "2026-08-22",
    read: "3 min",
    video: guideVideo(
      "bluesky",
      "connect-and-post-to-bluesky",
      "Connecting Bluesky with an app password.",
    ),
    body: [
      {
        type: "p",
        text: "Bluesky is the one connection that doesn't use a redirect. You'll paste two things: your handle, and an **app password**.",
      },
      {
        type: "image",
        src: "/help/connect-and-post-to-bluesky/01-bluesky-card.webp",
        alt: "The connect page with the Connect button on the Bluesky card highlighted.",
      },
      {
        type: "note",
        tone: "warning",
        title: "An app password is not your account password",
        text: "Your account password would give any tool full control of your account, including the ability to change that password. An app password is a separate, revocable credential — make one in the Bluesky app under **Settings → Privacy and security → App passwords**. The **Where do I get one?** link in the dialog goes straight there.",
      },
      {
        type: "image",
        src: "/help/connect-and-post-to-bluesky/02-app-password.webp",
        alt: "The Bluesky connect dialog with the Handle field, App password field and the Where do I get one link highlighted.",
      },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Handle",
            text: "The full handle, like you.bsky.social. A custom domain handle works too.",
          },
          {
            title: "App password",
            text: "Four groups of four characters. Paste it as Bluesky gives it to you, hyphens included.",
          },
          {
            title: "PDS URL — leave it alone",
            text: "It sits under Advanced and only matters if you self-host your Bluesky data. On a normal account, changing it will break the connection.",
          },
        ],
      },
      { type: "h2", text: "Writing the post" },
      {
        type: "p",
        text: "Bluesky allows **300 characters** and up to **4 images**. Longer thoughts go in a chain — **Add to thread** posts each segment as a reply to the one before it.",
      },
      { type: "h2", text: "Labels and languages" },
      {
        type: "image",
        src: "/help/connect-and-post-to-bluesky/03-content-labels.webp",
        alt: "The composer's Advanced options for Bluesky, showing the Languages field and the Content labels buttons.",
      },
      {
        type: "ul",
        items: [
          "**Content labels** — Adult, Sexual, Nudity or Graphic. Applying one puts the post behind a warning for people whose moderation settings ask for it. Labelling honestly is what keeps a post visible instead of getting the account actioned.",
          "**Languages** — up to three BCP-47 tags, like `en` or `pt-BR`. It tells Bluesky's feeds who to show the post to. Optional.",
        ],
      },
      {
        type: "note",
        tone: "warning",
        title: "Video needs a confirmed email",
        text: "Bluesky refuses video uploads from accounts with an unconfirmed email address, and the error it returns says nothing about email. If a video post fails and text posts work, confirm your address in the Bluesky app and retry.",
      },
    ],
    related: [
      "connect-and-post-to-threads",
      "connect-and-post-to-mastodon",
      "when-a-post-fails",
    ],
  },
  {
    slug: "connect-and-post-to-mastodon",
    links: [
      {
        blog: "how-to-schedule-mastodon-posts",
        label: "How to schedule Mastodon posts",
      },
      {
        blog: "mastodon-for-project-accounts",
        label: "Running a Mastodon account for a project",
      },
      {
        to: "/tools/mastodon-limits",
        label: "Check your Mastodon server's limits",
      },
    ],
    title: "Connect Mastodon from any server",
    excerpt:
      "Connect Mastodon from any server: pick your server first, then set visibility and content warnings, and write within that server's own limits.",
    category: "Connecting channels",
    updated: "2026-08-22",
    read: "3 min",
    video: guideVideo(
      "mastodon",
      "connect-and-post-to-mastodon",
      "Choosing your Mastodon server and authorizing there.",
    ),
    body: [
      {
        type: "p",
        text: "Mastodon isn't one website. It's thousands of independent servers that talk to each other, so the first thing we need is **which server your account is on**.",
      },
      {
        type: "image",
        src: "/help/connect-and-post-to-mastodon/01-mastodon-card.webp",
        alt: "The connect page with the Connect button on the Mastodon card highlighted.",
      },
      {
        type: "p",
        text: "The dialog lists the busiest servers. If yours isn't there, choose **Other server** and type its address — any Mastodon server works.",
      },
      {
        type: "image",
        src: "/help/connect-and-post-to-mastodon/02-choose-server.webp",
        alt: "The Mastodon connect dialog listing popular servers, with mastodon.social and Other server highlighted.",
        caption:
          "Your handle contains the answer: @you@server.social — the part after the second @ is the server.",
      },
      {
        type: "p",
        text: "You'll be sent to your own server to authorize, since that's where your account lives. Approve there and you land back here connected.",
      },
      { type: "h2", text: "Visibility, warnings and language" },
      {
        type: "p",
        text: "Mastodon's per-post controls live under **Advanced options**, and they're the ones the culture actually cares about.",
      },
      {
        type: "image",
        src: "/help/connect-and-post-to-mastodon/03-visibility-and-cw.webp",
        alt: "The composer's Advanced options for Mastodon, showing Visibility, Content warning and Mark as sensitive.",
      },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Visibility",
            text: "Public shows the post in public timelines. Unlisted is still visible to anyone with the link but stays out of those timelines — the usual choice for something you don't want amplified. Followers restricts it to your followers.",
          },
          {
            title: "Content warning",
            text: "A short subject line shown in place of the post, which readers tap to expand. Mastodon uses these far more liberally than other networks — for spoilers, politics, food, anything a reader might want to opt into.",
          },
          {
            title: "Mark as sensitive",
            text: "Blurs the attached media until it's tapped. Independent of the content warning, and worth using on anything startling.",
          },
          {
            title: "Language",
            text: "A single code like `en`. Servers use it for filtering, so setting it correctly gets the post to the right people.",
          },
        ],
      },
      { type: "h2", text: "Limits" },
      {
        type: "p",
        text: "The character limit is **your server's own**: PostDaily reads it when you connect and checks it again every day, so the composer's counter shows what your server allows. If a server doesn't publish its limit, the counter uses **500**, Mastodon's default. Attachments are held to **4** per post, the default most servers keep.",
      },
      {
        type: "note",
        tone: "info",
        title: "One server, one channel",
        text: "Accounts on different servers are separate channels with separate schedules. Connect each one and they'll appear side by side.",
      },
    ],
    related: [
      "connect-and-post-to-bluesky",
      "connect-and-post-to-threads",
      "plan-with-the-calendar",
    ],
  },
  {
    slug: "publish-to-instagram",
    links: [
      {
        help: "schedule-instagram-stories",
        label: "How to schedule Instagram Stories",
      },
      {
        limits: "instagram",
        label: "Instagram caption, hashtag and video limits",
      },
      { to: "/instagram-scheduler", label: "Instagram scheduler" },
    ],
    title: "Publish to Instagram",
    excerpt:
      "Schedule Instagram posts, Reels and Stories, plus the tagging, collaborator and cover controls that only appear once you pick a format.",
    category: "Composer & AI",
    updated: "2026-08-22",
    read: "4 min",
    video: guideVideo(
      "instagram",
      "publish-to-instagram",
      "Posts, Reels and Stories, with tagging and the Reel cover controls.",
    ),
    body: [
      {
        type: "p",
        text: "Instagram is the one network that will never accept a text-only post — every post needs at least one photo or video. Attach the media first and the rest of the composer unlocks around it.",
      },
      { type: "h2", text: "Pick the format first" },
      {
        type: "p",
        text: "**Post**, **Reel** or **Story**. This is the first choice to make, not the last: the controls underneath change with it, and a couple of them exist for one format only.",
      },
      {
        type: "image",
        src: "/help/publish-to-instagram/01-formats.webp",
        alt: "The Instagram composer with the Post, Reel and Story format tabs highlighted.",
      },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Post",
            text: "A feed post. One photo, or up to 10 photos as a carousel. Adding a video switches the post to a Reel, because PostDaily publishes Instagram videos as Reels.",
          },
          {
            title: "Reel",
            text: "A vertical video with its own cover and audio controls — see below.",
          },
          {
            title: "Story",
            text: "Disappears after 24 hours. Several images publish as separate frames, one after another.",
          },
        ],
      },
      { type: "h2", text: "Caption, alt text and the first comment" },
      {
        type: "image",
        src: "/help/publish-to-instagram/02-caption-tools.webp",
        alt: "The composer showing the ALT badge on the thumbnail, the Add comment button and the AI-Generated toggle.",
      },
      {
        type: "ul",
        items: [
          "**2,200 characters** for the caption. The counter beside the editor counts down.",
          "**ALT** on each thumbnail adds a description for people using a screen reader. Instagram shows it nowhere visible, which is exactly why it gets skipped — write it anyway.",
          "**Add comment** posts a comment on your own post the moment it publishes. This is where hashtags belong if you'd rather keep the caption clean.",
          "**AI-Generated** labels the post as AI-made. Instagram asks for this when it applies, and applying it yourself is better than having Instagram apply it for you.",
        ],
      },
      { type: "h2", text: "Tagging people, collaborators and products" },
      {
        type: "p",
        text: "On a **Post**, each thumbnail carries a tag button. It opens three tabs.",
      },
      {
        type: "image",
        src: "/help/publish-to-instagram/03-tagging.webp",
        alt: "The Instagram tag dialog with the People, Collaborators and Products tabs highlighted.",
        caption:
          "For people tags, tap the photo where the tag should sit, then type the username.",
      },
      {
        type: "steps",
        start: 4,
        items: [
          {
            title: "People",
            text: "Tags pinned to a spot on the image. Tap the photo first, then type the username — the tag goes where you tapped.",
          },
          {
            title: "Collaborators",
            text: "Invites up to three accounts to co-author the post, so it appears on their profile too. They have to be public, have collaborator invites turned on, and accept.",
          },
          {
            title: "Products",
            text: "Not available yet. Tagging products needs an Instagram Shop catalog, which PostDaily doesn't support so far.",
          },
        ],
      },
      {
        type: "note",
        tone: "warning",
        title: "Collaborators need the Facebook connection",
        text: "Collaborators are only available on an Instagram account connected **through a Facebook Page**. On a Direct Login account the tab explains this and stays empty — reconnect through Facebook to switch.",
      },
      {
        type: "note",
        tone: "info",
        title: "A refused collaborator won't lose the post",
        text: "If the invited account has collaborator invites turned off, or is private, Instagram rejects the invite. We publish the post again without it rather than let an optional tag cost you the post, and the dropped username is reported on the post afterwards.",
      },
      { type: "h2", text: "Reel options" },
      {
        type: "p",
        text: "Choosing **Reel** replaces the panel with controls that only make sense for video.",
      },
      {
        type: "image",
        src: "/help/publish-to-instagram/04-reel-options.webp",
        alt: "The Reel panel showing Also share to feed, the cover frame scrubber, Custom cover image and Rename original audio.",
      },
      {
        type: "steps",
        start: 7,
        items: [
          {
            title: "Also share to feed",
            text: "On by default. Leave it on and the Reel appears on your profile grid as well as in the Reels tab.",
          },
          {
            title: "Cover frame",
            text: "Drag to pick the thumbnail from the video itself. The live preview updates as you scrub, so you can see what you're choosing. Or use Custom cover image to upload one instead.",
          },
          {
            title: "Rename original audio",
            text: "Names the audio track. People who tap it see this name, and can use the sound in their own Reels.",
          },
        ],
      },
      { type: "h2", text: "If a post won't go out" },
      {
        type: "ul",
        items: [
          '**"Instagram posts require at least one photo or video"** — the composer is telling you the truth. Instagram has no text-only post.',
          "**More than 10 photos** — a carousel tops out at 10. Split the rest into a second post.",
          "**Personal account** — Instagram only allows scheduled publishing to Business and Creator accounts. Converting is free in the Instagram app.",
          "**A Story that partly published** — Instagram treats every frame as its own post against a daily cap, so a long Story can hit the limit midway. The post records which frames made it.",
        ],
      },
    ],
    related: [
      "connect-instagram",
      "facebook-reels-and-stories",
      "when-a-post-fails",
    ],
  },
  {
    slug: "schedule-instagram-stories",
    links: [
      { to: "/instagram-scheduler", label: "Instagram scheduler" },
      { limits: "instagram", label: "Instagram Story and Reel limits" },
      {
        help: "schedule-facebook-stories",
        label: "How to schedule Facebook Stories",
      },
    ],
    title: "How to schedule Instagram Stories",
    excerpt:
      "Photo and video Stories can be scheduled; link, poll and music stickers can't. Where to schedule them, what survives, and when to post from the app instead.",
    category: "Scheduling & queue",
    updated: "2026-09-27",
    read: "4 min",
    body: [
      {
        type: "p",
        text: "**You can schedule an Instagram Story made of photos or video, but not one with a link, poll, music or other interactive sticker.** Those stickers can only be added in the Instagram app when you post. The Instagram app's own scheduler doesn't cover Stories at all: it schedules posts, Reels and carousels. Stories are scheduled in Meta Business Suite or a tool like PostDaily.",
      },
      { type: "h2", text: "In Meta Business Suite" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Click Create story",
            text: "From the home page or the Content tab. Under **Post to**, choose your Instagram account, your Facebook Page, or both.",
          },
          {
            title: "Add up to ten photos or videos",
            text: "Business Suite's editor can add text, mentions, stickers and a link to each one.",
          },
          {
            title: "Choose Schedule and pick the time",
            text: "Scheduled Stories are under **Content → Stories → Scheduled**. These steps are from Meta's help centre, checked on 27 September 2026.",
          },
        ],
      },
      { type: "h2", text: "In PostDaily" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Attach photos or one video, and choose Story",
            text: "Several photos publish as consecutive frames, up to 10. A video Story is one video of 3 to 60 seconds, published on its own.",
          },
          {
            title: "Schedule it or add it to the queue",
            text: "It goes live at that time and disappears 24 hours later, like any Story.",
          },
        ],
      },
      { type: "h2", text: "What doesn't survive scheduling" },
      {
        type: "ul",
        items: [
          "**No interactive stickers.** Links, polls, questions, music and countdowns can't be added through Meta's publishing API, so no scheduler can include them.",
          "**No caption and no first comment.** A Story has no caption field, and Stories don't take comments.",
          "**No people tags or collaborators.** PostDaily doesn't send them on Stories.",
          "**Each frame counts toward Instagram's daily cap.** A 10-frame Story is 10 posts against the limit on how much an app can publish in a day, and a long Story can hit it part-way. The post records which frames went out.",
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "When to post from the app instead",
        text: "If the Story needs a link sticker, a poll or music, schedule a reminder for yourself and post it from the Instagram app at that time. Everything else — product shots, announcements, behind-the-scenes photos and short clips — schedules well.",
      },
      { type: "h2", text: "The same Story on Facebook" },
      {
        type: "p",
        text: "Select your Instagram account and your Facebook Page in the same post and choose **Story** for each. Facebook takes one photo or video per Story, so a Story going to both is limited to one item; schedule a multi-photo Instagram Story on its own. See [How to schedule Facebook Stories](/help/schedule-facebook-stories).",
      },
    ],
    related: ["publish-to-instagram", "connect-instagram"],
  },
  {
    slug: "connect-youtube",
    links: [
      {
        to: "/youtube-scheduler",
        label: "YouTube scheduler for Shorts and videos",
      },
    ],
    title: "Connect a YouTube channel",
    excerpt:
      "Connect a YouTube channel with one Google authorization, plus the daily upload limit worth knowing before you rely on it for Shorts and videos.",
    category: "Connecting channels",
    updated: "2026-09-24",
    read: "2 min",
    video: guideVideo(
      "youtube",
      "connect-youtube",
      "Connecting a YouTube channel, and the daily upload limit worth knowing.",
    ),
    body: [
      {
        type: "p",
        text: "You'll need a Google account that already has a **YouTube channel** on it. If the account has never created one, YouTube has nothing to publish to and won't appear after authorizing.",
      },
      {
        type: "p",
        text: "Open **Connect channel** and choose YouTube.",
      },
      {
        type: "image",
        src: "/help/connect-youtube/01-youtube-card.webp",
        alt: "The connect page with the Connect button on the YouTube card highlighted.",
      },
      {
        type: "p",
        text: "Google handles the rest. Approve the upload permission so scheduled videos and Shorts can publish, and you land back here connected.",
      },
      {
        type: "image",
        src: "/help/connect-youtube/02-authorize.webp",
        alt: "The YouTube connect dialog with the Continue with YouTube button highlighted.",
      },
      {
        type: "image",
        src: "/help/connect-youtube/03-connected.webp",
        alt: "The channels page listing the connected YouTube channel.",
      },
      { type: "h2", text: "Worth knowing" },
      {
        type: "note",
        tone: "info",
        title: "There is a daily upload ceiling",
        text: "YouTube counts video uploads against a separate daily quota from ordinary reads, and counts it per app rather than per channel. PostDaily's allowance is generous, and a post that does reach it is retried automatically over the next few hours.",
      },
      { type: "h2", text: "If it won't connect" },
      {
        type: "ul",
        items: [
          "**No channel appears after authorizing** — the Google account has no YouTube channel yet. Create one at youtube.com, then connect again.",
          "**You picked the wrong Google account** — sign out of Google, or use the account picker on the consent screen, then retry.",
          "**Brand accounts** — pick the brand channel rather than your personal one on the consent screen if you manage several.",
        ],
      },
    ],
    related: ["publish-to-youtube", "connect-instagram", "when-a-post-fails"],
  },
  {
    slug: "publish-to-youtube",
    links: [{ to: "/youtube-scheduler", label: "YouTube scheduler" }],
    title: "Upload to YouTube",
    excerpt:
      "Schedule a YouTube upload: the one required field that decides whether the post saves at all, then title, tags, visibility and the Shorts switch.",
    category: "Composer & AI",
    updated: "2026-09-24",
    read: "4 min",
    video: guideVideo(
      "youtube",
      "publish-to-youtube",
      "The one field that blocks a post, then title, tags and visibility.",
    ),
    body: [
      {
        type: "p",
        text: "YouTube takes one video per post. Attach it first: the whole panel fills in around the file, and the composer picks **Short** or **Video** for you based on the shape of what you attached.",
      },
      { type: "h2", text: "Audience is not optional" },
      {
        type: "p",
        text: "**Audience** is the one field that will stop a post from saving. YouTube requires every upload to declare whether it is made for children, and there is no default — leave it on **Choose…** and the composer holds the post back.",
      },
      {
        type: "image",
        src: "/help/publish-to-youtube/01-audience-required.webp",
        alt: "The YouTube composer with the Audience dropdown and the validation message about declaring whether the video is made for kids.",
        caption:
          "The only blocking field on a YouTube post. Set it once per post.",
      },
      {
        type: "note",
        tone: "warning",
        title: "Answer it honestly",
        text: "This is a legal declaration, not a preference. Marking a video as made for kids turns off comments and personalised ads on it; marking one wrongly is a matter between you and YouTube.",
      },
      { type: "h2", text: "Title and format" },
      {
        type: "image",
        src: "/help/publish-to-youtube/02-title-and-format.webp",
        alt: "The Video title field and the Video / Short format options.",
      },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Video title",
            text: "Up to 100 characters, and separate from your post text — leave it empty and the first line of the post becomes the title.",
          },
          {
            title: "Video or Short",
            text: "Picked for you from the video's shape, and changeable. Short adds #Shorts so YouTube files it correctly.",
          },
        ],
      },
      { type: "h2", text: "Description, tags and category" },
      {
        type: "image",
        src: "/help/publish-to-youtube/03-details.webp",
        alt: "The Description, Tags and Category fields in the YouTube panel.",
      },
      {
        type: "ul",
        items: [
          "**Description** — 5,000 bytes, which is 5,000 characters of plain English text but fewer with emoji or non-Latin scripts. Left empty, it uses your post text, minus the first line if that line became the title.",
          "**Tags** — comma-separated, 500 characters for all of them together, not each.",
          "**Category** — defaults to People & Blogs. It affects where YouTube recommends the video.",
        ],
      },
      { type: "h2", text: "Visibility and the rest" },
      {
        type: "image",
        src: "/help/publish-to-youtube/04-visibility.webp",
        alt: "Visibility, Notify subscribers, License, Allow embedding and Show view count.",
      },
      {
        type: "steps",
        start: 3,
        items: [
          {
            title: "Visibility",
            text: "Public, Unlisted or Private. Unlisted means anyone with the link can watch, but it appears nowhere on your channel.",
          },
          {
            title: "Notify subscribers",
            text: "On by default. Turn it off for a re-upload or something minor, so you don't notify everyone twice.",
          },
          {
            title: "Allow embedding and Show view count",
            text: "Whether other sites can play the video, and whether the watch page shows its view and rating counts.",
          },
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "Two smaller wins",
        text: "Setting a **spoken language** makes the video eligible for YouTube's auto-dubbing and helps it surface in searches in that language. And attaching a **JPEG or PNG alongside the video** sets it as the custom thumbnail — that one needs a channel eligible for custom thumbnails.",
      },
      { type: "h2", text: "If something looks wrong afterwards" },
      {
        type: "ul",
        items: [
          "**A day's uploads stop going out** — video uploads draw on a separate daily quota from everything else, and it is counted per app rather than per channel.",
          "**The title isn't what you wrote** — an empty Video title takes the first line of the post instead. Fill the field in to control it.",
        ],
      },
    ],
    related: [
      "publish-to-instagram",
      "facebook-reels-and-stories",
      "when-a-post-fails",
    ],
  },
  {
    slug: "media-library",
    title: "Organise your media library",
    excerpt:
      "Upload images and videos once and reuse them in any post, with collections, filters and favourites to keep a growing media library findable.",
    category: "Composer & AI",
    updated: "2026-08-20",
    read: "3 min",
    video: guideVideo(
      "product",
      "media-library",
      "Reusing what you have already uploaded, filtered and collected.",
    ),
    body: [
      {
        type: "p",
        text: "Everything you attach to a post lives in your **media library**. Uploading it here once means you can reuse it across posts and channels without hunting through your downloads folder again.",
      },
      { type: "h2", text: "Add files" },
      {
        type: "p",
        text: "Drag files straight onto the page, or use **Choose files**. Images, video and PDFs are all accepted, up to 250 MB each.",
      },
      {
        type: "image",
        src: "/help/media-library/01-library.webp",
        alt: "The media library page with the Choose files button highlighted.",
      },
      { type: "h2", text: "Find things again" },
      {
        type: "p",
        text: "Filter by type, search by filename, and sort by newest or largest. Once you have a few dozen files, these are what keep the library usable.",
      },
      {
        type: "image",
        src: "/help/media-library/02-filters.webp",
        alt: "The media library filters showing type tabs and the sort control highlighted.",
      },
      { type: "h2", text: "Collections" },
      {
        type: "p",
        text: "Collections group files by campaign, client or month — whatever fits how you work. A file can belong to more than one collection, so you don't have to duplicate anything.",
      },
      {
        type: "image",
        src: "/help/media-library/03-collections.webp",
        alt: "The media library sidebar with the New collection button highlighted.",
      },
      {
        type: "note",
        tone: "info",
        title: "Favourites are a quick shortcut",
        text: "Star the logo, brand assets and anything else you reach for constantly — they'll sit in Favourites, one click from the composer.",
      },
    ],
    related: ["publish-your-first-facebook-post", "facebook-reels-and-stories"],
  },
  {
    slug: "invite-your-team",
    title: "Add team members and set their roles",
    excerpt:
      "Invite teammates by email as an Admin, Editor or Viewer. Seats come with your plan rather than being billed per person, and each role's limits are spelled out.",
    category: "Team & workspace",
    updated: "2026-09-27",
    read: "2 min",
    video: guideVideo(
      "product",
      "invite-your-team",
      "Adding a workspace member, and how seats come from your plan.",
    ),
    body: [
      {
        type: "p",
        text: "A workspace can be shared with as many teammates as your plan allows. Everyone works from the same channels, queue and media library.",
      },
      { type: "h2", text: "Send an invite" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Open Workspace settings → Members",
            text: "You'll find it at the bottom of the sidebar.",
          },
          {
            title: "Enter their email, pick a role and click Send invite",
            text: "They don't need a PostDaily account yet — the invite works either way, and walks them through signing up.",
          },
        ],
      },
      {
        type: "image",
        src: "/help/invite-your-team/01-members.webp",
        alt: "The Members settings page with the email field and Send invite button highlighted.",
      },
      {
        type: "note",
        tone: "info",
        title: "Invites are bound to the email you send them to",
        text: "The link only works for that address, so a forwarded invite can't be used by someone else.",
      },
      { type: "h2", text: "Roles" },
      {
        type: "ul",
        items: [
          "**Admin** — Manage channels, members, billing, and workspace settings. Can also create and publish posts.",
          "**Editor** — Create, schedule, and publish posts. Manage media, templates, tags, and the queue.",
          "**Viewer** — View posts, analytics, media, and channels. Cannot edit content or change settings.",
        ],
      },
      {
        type: "p",
        text: "The person who created the workspace is its **Owner**. An Admin can invite Editors and Viewers; only the Owner can make someone an Admin. Clients who only need to approve posts don't need a seat at all: send them a review link instead.",
      },
      { type: "h2", text: "Seats" },
      {
        type: "p",
        text: "Each member takes a seat on your plan. If you're at the limit, the invite is refused and tells you so — upgrade, or remove a member you no longer work with.",
      },
      {
        type: "note",
        tone: "warning",
        title: "Removing a member is immediate",
        text: "They lose access to the workspace straight away. Anything they scheduled stays exactly where it is — removing a person never deletes their posts.",
      },
    ],
    related: ["client-approvals", "manage-your-plan"],
  },
  {
    slug: "client-approvals",
    links: [
      {
        blog: "client-approval-workflow-freelancers",
        label: "An approval workflow for freelancers with one client",
      },
      {
        to: "/client-approvals",
        label: "Client approval in PostDaily and 9 other schedulers",
      },
      {
        compare: "planable",
        label: "How approvals work in PostDaily and Planable",
      },
      {
        compare: "sprout-social",
        label: "No-login approval in PostDaily and Sprout Social",
      },
    ],
    title: "Client approval with a no-login link",
    excerpt:
      "Send clients a review link that needs no login or seat, so they can approve posts or leave feedback before they publish. Rotate or revoke it any time.",
    category: "Team & workspace",
    updated: "2026-08-20",
    read: "3 min",
    video: guideVideo(
      "product",
      "client-approvals",
      "A no-login review link for whoever approves the work.",
    ),
    body: [
      {
        type: "p",
        text: "Clients rarely want another login. **Approval links** let you share scheduled posts with anyone — they open a read-only review page, approve or leave feedback, and never create an account.",
      },
      { type: "h2", text: "Create a link" },
      {
        type: "image",
        src: "/help/client-approvals/01-approvals.webp",
        alt: "The Approvals settings page with the New link button highlighted.",
      },
      {
        type: "p",
        text: "Once created, copy the link and send it however you like — email, Slack, a message. Anyone with the link can review; nobody with it can edit or publish.",
      },
      {
        type: "image",
        src: "/help/client-approvals/02-link-actions.webp",
        alt: "An approval link with the Copy link and Revoke link actions highlighted.",
      },
      { type: "h2", text: "Ending access" },
      {
        type: "ul",
        items: [
          "**Rotate** issues a fresh link and immediately breaks the old one — useful if a link was shared too widely.",
          "**Revoke** switches it off entirely.",
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "Links only ever expose what you shared",
        text: "A review link is scoped to its own workspace and the posts on it. It can't be used to reach anything else in your account, and media is served through short-lived signed URLs.",
      },
    ],
    related: ["invite-your-team", "facebook-scheduling-and-queue"],
  },
  {
    slug: "manage-your-plan",
    links: [
      {
        compare: "buffer",
        label: "How PostDaily compares with Buffer",
      },
      {
        compare: "hootsuite",
        label: "How PostDaily compares with Hootsuite",
      },
      {
        to: "/alternatives",
        label: "Every comparison, with who each tool suits",
      },
      {
        blog: "buffer-pricing",
        label: "What Buffer costs at 3, 10 and 25 channels",
      },
      {
        blog: "hootsuite-pricing",
        label: "Hootsuite pricing, and whether it has a free plan",
      },
    ],
    title: "Manage your plan and billing",
    excerpt:
      "See what your plan is using, compare plans, upgrade or downgrade, update your payment method or cancel, and find every invoice in the billing portal.",
    category: "Billing & plans",
    updated: "2026-08-20",
    read: "2 min",
    video: guideVideo(
      "product",
      "manage-your-plan",
      "Your plan, what you are using against it, and the billing portal.",
    ),
    body: [
      {
        type: "p",
        text: "**Workspace settings → Billing** shows your current plan, how much of each limit you're using, and what changes if you move tier.",
      },
      {
        type: "image",
        src: "/help/manage-your-plan/01-billing.webp",
        alt: "The billing page with the Manage subscription button highlighted.",
      },
      { type: "h2", text: "Changing plan" },
      {
        type: "p",
        text: "**Manage subscription** opens the billing portal, where you can upgrade, downgrade, update your payment method, or cancel. Invoices live there too.",
      },
      {
        type: "note",
        tone: "info",
        title: "Plans apply across every workspace you own",
        text: "Your plan belongs to your account, not to one workspace — every workspace you own inherits it. Usage limits are still counted per workspace.",
      },
      { type: "h2", text: "Comparing tiers" },
      {
        type: "p",
        text: "Expand the plan details to see every tier side by side, with the real limits rather than marketing copy.",
      },
      {
        type: "image",
        src: "/help/manage-your-plan/02-plan-details.webp",
        alt: "The billing page showing the expanded plan comparison table.",
      },
    ],
    related: ["invite-your-team", "facebook-analytics"],
  },
  {
    slug: "two-factor-authentication",
    title: "Turn on two-factor authentication",
    excerpt:
      "Add a second sign-in step by scanning a QR code with any authenticator app, then save your backup codes somewhere safe before you finish.",
    category: "Account & security",
    updated: "2026-08-20",
    read: "2 min",
    video: guideVideo(
      "product",
      "two-factor-authentication",
      "Adding a second factor to the account that holds every channel.",
    ),
    body: [
      {
        type: "p",
        text: "Two-factor authentication means a stolen password isn't enough to reach your account. Given PostDaily holds publishing access to your social channels, it's worth the minute it takes.",
      },
      { type: "h2", text: "Enable it" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Open Account → Security and click Enable 2FA",
            text: "You'll scan a QR code with any authenticator app — 1Password, Authy, Google Authenticator, whichever you already use.",
          },
        ],
      },
      {
        type: "image",
        src: "/help/two-factor-authentication/01-security.webp",
        alt: "The Security settings page with the Enable 2FA button highlighted.",
      },
      {
        type: "note",
        tone: "warning",
        title: "Save your backup codes before you finish",
        text: "Backup codes are shown once, when you turn 2FA on. They're how you get back in if you lose your phone — store them in your password manager, not on the phone itself.",
      },
      { type: "h2", text: "Changing your password" },
      {
        type: "p",
        text: "The same page is where you change your password. Doing so signs out your other sessions, so anyone using a stolen one is dropped immediately.",
      },
      {
        type: "image",
        src: "/help/two-factor-authentication/02-change-password.webp",
        alt: "The Security settings page with the Change password button highlighted.",
      },
    ],
    related: ["invite-your-team", "manage-your-plan"],
  },
];

/**
 * Troubleshooting and reference articles written for the queries people type
 * when something is wrong — "why didn't my scheduled post publish", "bluesky
 * character limit". They carry no screenshots on purpose: the capture script
 * (`scripts/guide-capture`) drives the real app for those, and an article that
 * is useful as text should not wait on a screenshot to ship.
 */
const TROUBLESHOOTING_ARTICLES: HelpArticle[] = [
  {
    slug: "channel-stopped-working",
    links: [
      { blog: "why-scheduled-posts-fail", label: "Why scheduled posts fail" },
    ],
    title: "A channel stopped working",
    excerpt:
      "What 'Waiting on reconnect' means, why your scheduled posts were parked rather than failed, and how reconnecting restores the whole queue.",
    category: "Troubleshooting",
    updated: "2026-08-31",
    read: "3 min",
    body: [
      {
        type: "p",
        text: "Every social network expires the access it grants an app. Change your password, revoke access, lose admin rights on a Page, or simply wait long enough, and the connection stops working. PostDaily tries to renew connections automatically in the background, so most of the time you never see this. When renewal genuinely fails, the channel is marked as needing reconnection.",
      },
      { type: "h2", text: "Your scheduled posts were parked, not lost" },
      {
        type: "p",
        text: "This is the part worth knowing. When a channel breaks, every post already queued behind it is moved to **Waiting on reconnect** rather than being attempted and failed. Nothing was sent, nothing is wrong with the posts, and you do not get a failure email for each one.",
      },
      {
        type: "p",
        text: "The alternative — which is what most schedulers do — is to let the queue run into the broken connection and fail one post at a time, at each scheduled slot, generating a notification for every one. You would come back to a column of failures and no queue left.",
      },
      {
        type: "note",
        tone: "info",
        title: "Reconnecting restores them automatically",
        text: "Parked posts return to Scheduled on their own once the channel works again. You do not need to find them, re-create them, or reschedule anything by hand.",
      },
      { type: "h2", text: "Reconnect the channel" },
      {
        type: "steps",
        items: [
          {
            title: "Open Channels",
            text: "The channel needing attention is flagged in the list.",
          },
          {
            title: "Choose Reconnect",
            text: "You are sent to the network's own authorization screen, exactly as when you first connected.",
          },
          {
            title: "Approve the same permissions",
            text: "Approve every permission requested. Declining one silently removes a capability — first comments or analytics usually — and the channel will look connected while quietly failing at that one thing.",
          },
          {
            title: "Check the queue",
            text: "Parked posts flip back to Scheduled. Anything whose slot passed while the channel was broken stays parked so it does not publish unexpectedly late — reschedule those yourself.",
          },
        ],
      },
      { type: "h2", text: "If it breaks again immediately" },
      {
        type: "p",
        text: "A channel that needs reconnecting again within minutes usually means the underlying access is being revoked, not expiring. The common causes:",
      },
      {
        type: "ul",
        items: [
          "**You lost admin rights** on the Facebook Page or the account. Access follows your role — if someone removed it, reconnecting cannot restore it.",
          "**The password changed** on the network account. Some networks invalidate every app authorization when that happens.",
          "**An app password was revoked.** For Bluesky, check the app password you used still exists in Bluesky's settings.",
          "**Someone removed the app** in the network's connected-apps or business-integrations settings.",
        ],
      },
      {
        type: "p",
        text: "One transient error from a network does not mark a channel as broken — it takes repeated failures, so a momentary outage on their side will not disconnect you.",
      },
    ],
    related: ["when-a-post-fails", "connect-a-facebook-page"],
  },
  {
    slug: "bluesky-video-not-uploading",
    links: [
      {
        to: "/tools/video-checker",
        label: "Check whether a video will upload",
      },
      { blog: "why-scheduled-posts-fail", label: "Why scheduled posts fail" },
    ],
    title: "Bluesky video uploads are failing",
    excerpt:
      "Bluesky video uploads failing is almost always an unconfirmed email address on the account. Why text and images still work, and the fix.",
    category: "Troubleshooting",
    updated: "2026-08-31",
    read: "2 min",
    body: [
      {
        type: "p",
        text: "If your Bluesky text and image posts publish perfectly but video is rejected, the cause is almost never your connection. Bluesky runs video through a separate service with its own rules, and the most common one to trip is that **the account's email address must be confirmed**.",
      },
      {
        type: "note",
        tone: "warning",
        title: "An error here does not mean a dead connection",
        text: "Video failing while everything else works is the signature of this problem. Reconnecting the channel will not fix it, because the connection was never broken.",
      },
      { type: "h2", text: "Confirm your email in Bluesky" },
      {
        type: "steps",
        items: [
          {
            title: "Open Bluesky's own app or website",
            text: "This has to be done in Bluesky, not in PostDaily.",
          },
          {
            title: "Go to Settings, then Account",
            text: "If the email is unconfirmed, Bluesky shows a prompt to verify it.",
          },
          {
            title: "Confirm from the email Bluesky sends",
            text: "Check spam if it does not arrive within a few minutes.",
          },
          {
            title: "Reconnect the channel in PostDaily",
            text: "Open Channels, choose the Bluesky channel and reconnect, so the video service is authorized against the now-confirmed account.",
          },
        ],
      },
      { type: "h2", text: "Other things worth checking" },
      {
        type: "ul",
        items: [
          "**File size and length.** Bluesky caps both, and a long or high-bitrate clip can be rejected where a shorter one succeeds.",
          "**Format.** MP4 with H.264 video and AAC audio is the safest combination. PostDaily converts common formats automatically, but an unusual codec can still fail.",
          "**Daily limits.** Bluesky limits how much video an account can upload per day. If uploads worked earlier and stopped, try again tomorrow before assuming anything is broken.",
        ],
      },
      {
        type: "p",
        text: "If video still fails after confirming your email, open the failed post — PostDaily records the message Bluesky itself returned, which usually names the real cause.",
      },
    ],
    related: ["connect-and-post-to-bluesky", "when-a-post-fails"],
  },
  {
    slug: "character-limits-by-network",
    links: [
      {
        to: "/tools/character-counter",
        label: "Character counter for every network",
      },
      { to: "/limits", label: "Video, image and text limits by network" },
      { to: "/twitter-scheduler", label: "X (Twitter) scheduler" },
      { to: "/threads-scheduler", label: "Threads scheduler" },
      {
        to: "/tools/thread-splitter",
        label: "Split a long post into a thread",
      },
      {
        to: "/tools/mastodon-limits",
        label: "Check your Mastodon server's limit",
      },
      {
        blog: "mastodon-character-limit",
        label: "Why your Mastodon server's limit may not be 500",
      },
      {
        blog: "bluesky-threads-guide",
        label: "Write a Bluesky thread people actually read",
      },
    ],
    title: "How each network counts characters",
    excerpt:
      "Every network's limit in one place — plus why the counter disagrees with a plain character count on Bluesky, Mastodon and X.",
    category: "Composer & AI",
    updated: "2026-09-08",
    read: "4 min",
    body: [
      {
        type: "p",
        text: "The composer's counter is not counting the letters in your post. It counts the way the network you are posting to counts, which is different on almost every network and is the reason a post can look fine in one editor and be rejected by the API.",
      },
      { type: "h2", text: "The limits" },
      {
        type: "ul",
        items: [
          "**Bluesky** — 300 characters",
          "**X** — 280 characters",
          "**Mastodon** — 500 characters by default, but each server sets its own and many raise it",
          "**Threads** — 500 characters",
          "**Pinterest** — 800 characters",
          "**Instagram** — 2,200 characters",
          "**TikTok** — 2,200 characters",
          "**LinkedIn** — 3,000 characters",
          "**YouTube** — 5,000 bytes in the description (fewer characters with emoji or non-Latin text)",
          "**Facebook** — 63,206 characters",
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "Posting to several networks at once",
        text: "The counter shows the tightest limit among the channels you have selected, so you cannot accidentally write something only half your channels will accept. Give a channel its own text in the composer to write past that.",
      },
      { type: "h2", text: "Why the number moves when you paste a link" },
      {
        type: "p",
        text: "**Mastodon** counts every link as 23 characters no matter how long the URL is, and ignores the domain part of a mention entirely — so `@someone@a-very-long-instance.example` costs the same as `@someone`. A post full of links has far more room than a plain character count suggests. Both figures are per-server settings: [check your Mastodon server's actual limit](/tools/mastodon-limits) to see what yours allows.",
      },
      {
        type: "p",
        text: "**X** does something similar but not identical: links count as a fixed weight, and characters from scripts such as Chinese, Japanese and Korean count double.",
      },
      { type: "h2", text: "Why emoji can cost more than one character" },
      {
        type: "p",
        text: "**Bluesky** counts grapheme clusters — what a human sees as one character. A family emoji is a single character to Bluesky even though it is built from several code points joined together. Naive editors count the underlying units and tell you a post is far longer than it is.",
      },
      {
        type: "p",
        text: "Bluesky also enforces a separate byte ceiling underneath the 300-character limit. For a long post with many emoji, that byte limit is usually what you hit first.",
      },
      {
        type: "note",
        tone: "info",
        title: "The counter and the scheduler agree",
        text: "The composer's counter and the check that runs when you schedule call the same function, and the publishing adapters count the same way. A post the counter accepts can be scheduled.",
      },
      { type: "h2", text: "When a post is too long anyway" },
      {
        type: "ul",
        items: [
          "**Write a thread.** On Bluesky, Mastodon, X and Threads you can split a post into explicit segments, each with its own media, published as a real reply chain.",
          "**Give one channel its own text.** Write a short version for Bluesky and a long one for LinkedIn in the same post rather than compromising on both.",
        ],
      },
    ],
    related: ["write-with-ai", "connect-and-post-to-bluesky"],
  },
];

/**
 * Connecting outside AI apps through PostDaily's MCP server (`docs/mcp/`).
 * Text only, like the troubleshooting set: the setup happens inside the AI
 * app, which the capture script cannot drive. Labels and wording follow
 * Settings → AI agents and the consent page, and the per-app steps follow
 * `components/agents/client-guides.ts` — change them together.
 */
const AI_AGENT_ARTICLES: HelpArticle[] = [
  {
    slug: "connect-ai-apps",
    links: [
      {
        to: "/ai-agents",
        label: "What you can ask an AI app to do in PostDaily",
      },
    ],
    title: "Connect Claude, ChatGPT and other AI apps",
    excerpt:
      "Add PostDaily to Claude, ChatGPT, Cursor, Codex or any MCP app, choose what it can do, and draft, schedule and publish posts from a chat.",
    category: "AI agents",
    updated: "2026-10-02",
    read: "4 min",
    body: [
      {
        type: "p",
        text: "PostDaily runs an MCP server, the standard way AI apps connect to other tools. Once you add it, an app like Claude or ChatGPT can list your channels, write and check posts against each network's rules, schedule them into your queue and read your analytics — all in your own workspaces, with the same plan limits and approvals as the PostDaily app.",
      },
      {
        type: "p",
        text: "It works on every plan. The server URL is **https://mcp.postdaily.app/mcp**, and you'll also find it in **Settings → AI agents** with a setup guide for each app.",
      },
      { type: "h2", text: "Connect an app that signs in with PostDaily" },
      {
        type: "p",
        text: "Most apps — Claude, ChatGPT, Claude Code, Codex, Cursor, VS Code, Gemini CLI, Windsurf, Zed, Goose and LM Studio — need only the server URL. They send you to PostDaily to approve access, so you never paste a password or key into the app.",
      },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Add PostDaily as a connector in your AI app",
            text: "In Claude, open **Customize → Connectors**, select **+** and add PostDaily from the directory. In ChatGPT, turn on **Developer mode** under **Settings → Security and login**, then add PostDaily under **Plugins** with the server URL. Other apps have their own steps in Settings → AI agents, several with a one-click install button.",
          },
          {
            title: "Sign in to PostDaily when the app opens it",
            text: "If you last signed in more than a day ago, PostDaily asks you to sign in again before it lets you grant access.",
          },
          {
            title: "Choose what the app can do and which workspaces it can use",
            text: "Pick an access level (below) and tick the workspaces. In a workspace where you're a viewer, the app can only read.",
          },
          {
            title: "Select Allow access",
            text: "You're returned to the app, which can now use PostDaily. Try asking it to list your channels.",
          },
        ],
      },
      { type: "h2", text: "Claude Code: install the plugin" },
      {
        type: "p",
        text: "In Claude Code, the PostDaily plugin adds the server plus skills for uploading files from your computer and turning a release into posts, and it asks you before anything publishes right away, deletes or cancels a post, or schedules a batch.",
      },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Add PostDaily's plugin marketplace",
            text: "In your terminal, run **claude plugin marketplace add mehedihasan2810/postdaily-claude-plugin**",
          },
          {
            title: "Install the plugin",
            text: "Run **claude plugin install postdaily@postdaily**",
          },
          {
            title: "Sign in",
            text: "In Claude Code, run **/mcp**, choose **plugin:postdaily:postdaily** and authenticate. PostDaily opens in your browser to approve access, as above.",
          },
        ],
      },
      { type: "h2", text: "Codex: install the plugin" },
      {
        type: "p",
        text: "In Codex, the PostDaily plugin adds the server plus the same skills for uploading files and turning a release into posts, and Codex asks you before every call that can publish, schedule, delete or cancel a post.",
      },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Add PostDaily's plugin marketplace",
            text: "In your terminal, run **codex plugin marketplace add mehedihasan2810/postdaily-codex-plugin**",
          },
          {
            title: "Install the plugin",
            text: "Run **codex plugin add postdaily@postdaily**",
          },
          {
            title: "Sign in",
            text: "Run **codex mcp login postdaily**. PostDaily opens in your browser to approve access, as above.",
          },
        ],
      },
      { type: "h2", text: "Adding your own photos and videos" },
      {
        type: "p",
        text: "Chat apps such as Claude and ChatGPT can't open files on your phone or computer. Ask the app for an upload link: it gives you a PostDaily link that opens your media library in the right workspace. Upload your images, videos or PDFs there, then tell the app you're done and it attaches them to your post.",
      },
      {
        type: "p",
        text: "Coding agents such as Claude Code, Codex and Cursor can upload a file straight from your computer instead — just tell them which file to use.",
      },
      { type: "h2", text: "Access levels" },
      {
        type: "ul",
        items: [
          "**Full access** — create, schedule, publish, reschedule, cancel and delete posts; upload media; read everything.",
          "**Drafts only** — create and edit drafts and upload media. Nothing is scheduled or published until someone does it in PostDaily.",
          "**Read only** — read channels, posts, queue and analytics. Cannot change anything.",
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "It asks before publishing right away",
        text: "Even with full access, an AI app has to confirm with you in the chat before it publishes a post immediately or retries a failed one. Scheduling into your queue doesn't need that step, and you can still review or cancel a scheduled post in PostDaily.",
      },
      { type: "h2", text: "Use an API key instead" },
      {
        type: "p",
        text: "Some apps and automations can't sign in with PostDaily — Cline, JetBrains AI Assistant, n8n, Make and your own scripts. They use an API key and a separate URL, **https://mcp.postdaily.app/key/mcp**.",
      },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Open Settings → AI agents and select API key",
            text: "Give it a name you'll recognise later, choose an access level and workspaces, and pick when it expires — 30 days, 90 days, 1 year or never.",
          },
          {
            title: "Copy the key straight away",
            text: "It's shown only once. Paste it into your app as a bearer token (Authorization: Bearer followed by the key), or store it in a password manager.",
          },
        ],
      },
      {
        type: "note",
        tone: "warning",
        title: "Treat a key like a password",
        text: "Anyone with the key can act in the workspaces you chose. If one leaks, revoke it in Settings → AI agents — it stops working on its next request.",
      },
      { type: "h2", text: "Disconnect an app or revoke a key" },
      {
        type: "p",
        text: "**Settings → AI agents** lists every connected app and API key with the workspaces it can use and when it was last used. **Disconnect** or **Revoke** takes effect on its next request.",
      },
      {
        type: "ul",
        items: [
          "**Changing or resetting your password** disconnects every AI app and revokes every API key you created, so connect them again afterwards.",
          "**An app you haven't used for 60 days** has to sign in to PostDaily again.",
          "**Leaving a workspace**, or being removed from it, removes that workspace from your connected apps too.",
        ],
      },
      { type: "h2", text: "If the app can't connect" },
      {
        type: "ul",
        items: [
          "**Check the URL.** Apps that sign in use the address ending in /mcp; API keys use the one ending in /key/mcp. Cursor ignores a key sent to the first one.",
          "**It says it can't find a channel.** The app only sees workspaces you ticked when you approved it. Disconnect it and connect again to choose different ones.",
          "**A tool is missing.** An app with Drafts only or Read only access isn't offered the tools it isn't allowed to use. Connect again with a higher access level if it needs them.",
        ],
      },
    ],
    related: [
      "connect-claude",
      "connect-chatgpt",
      "connect-cursor",
      "connect-claude-code",
      "connect-codex",
      "connect-with-an-api-key",
    ],
  },
  {
    slug: "connect-claude",
    links: [
      {
        to: "/ai-agents",
        label: "What you can ask an AI app to do in PostDaily",
      },
      {
        blog: "how-to-schedule-social-media-posts-with-claude",
        label: "How to schedule social media posts with Claude",
      },
    ],
    title: "Connect PostDaily to Claude",
    excerpt:
      "Add PostDaily to Claude from the connector directory on any plan, approve access, and draft, schedule and publish posts from a chat on web, desktop or phone.",
    category: "AI agents",
    updated: "2026-10-02",
    read: "3 min",
    body: [
      {
        type: "p",
        text: "PostDaily is listed in Claude's connector directory, so you can add it on any Claude plan, Free included. Once it's connected, it works in Claude on the web, in Claude Desktop and in the Claude apps for iOS and Android.",
      },
      { type: "h2", text: "Add PostDaily from the directory" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "In Claude, open **Customize → Connectors**",
            text: "Or, in a chat, select **+**, hover over **Connectors** and choose **Manage connectors**.",
          },
          {
            title: "Select **+** next to Connectors and find PostDaily",
            text: "Search the directory for PostDaily and open its listing.",
          },
          {
            title: "Select **Connect**",
            text: "PostDaily opens in a new window. Sign in if it asks you to. If you last signed in more than a day ago, it asks again before it lets you grant access.",
          },
          {
            title: "Choose what Claude can do, then select **Allow access**",
            text: "Pick **Full access**, **Drafts only** or **Read only**, and tick the workspaces Claude may use. You're returned to Claude when you're done.",
          },
        ],
      },
      { type: "h2", text: "Turn it on in a chat" },
      {
        type: "p",
        text: 'Claude uses a connector only when it\'s switched on for the conversation. Select **+** (or type `/`), hover over **Connectors** and switch PostDaily on. Then ask something simple to check it works, such as "List my PostDaily channels."',
      },
      { type: "h2", text: "Things to ask" },
      {
        type: "ul",
        items: [
          '"Write a post about our spring sale for Instagram and Facebook and add it to my queue."',
          "\"What's scheduled for next week? Move Thursday's post to Friday at 10am.\"",
          '"Turn this article into a Bluesky thread and schedule it for tomorrow morning."',
          '"Which posts did best last month, and when do my channels get the most engagement?"',
        ],
      },
      {
        type: "note",
        tone: "info",
        title: "Claude asks before anything goes out right away",
        text: "Even with full access, Claude has to get your go-ahead in the chat before it publishes a post immediately or retries a failed one. A scheduled post waits in your queue, where you can still edit or cancel it.",
      },
      { type: "h2", text: "Photos and videos" },
      {
        type: "p",
        text: "Claude can't open files on your phone or computer by itself. Ask it for an upload link: it gives you a PostDaily link that opens your media library in the right workspace. Upload your images, videos or PDFs there, tell Claude you're done, and it attaches them to the post. It can also add an image from a public web address.",
      },
      { type: "h2", text: "Team, Enterprise and custom connectors" },
      {
        type: "ul",
        items: [
          "**Team and Enterprise:** an Owner adds connectors for the organisation in **Organization settings → Connectors**. Members then open **Customize → Connectors** and select **Connect** next to PostDaily.",
          "**Adding it as a custom connector:** in **Customize → Connectors**, select **+ Add**, then **Add custom connector**. Name it PostDaily, paste **https://mcp.postdaily.app/mcp**, select **Continue**, keep the default sign-in settings and select **Add**.",
          "**On Claude Free** you can add only one custom connector, so use the directory listing instead. It doesn't count towards that limit.",
        ],
      },
      { type: "h2", text: "Disconnect Claude" },
      {
        type: "p",
        text: "In PostDaily, open **Settings → AI agents** and select **Disconnect** next to Claude. It stops working on its next request. Removing the connector in Claude hides it there, but disconnecting in PostDaily is what revokes its access.",
      },
    ],
    related: ["connect-ai-apps", "connect-claude-code", "connect-chatgpt"],
  },
  {
    slug: "connect-chatgpt",
    links: [
      {
        to: "/ai-agents",
        label: "What you can ask an AI app to do in PostDaily",
      },
      {
        blog: "how-to-schedule-social-media-posts-with-chatgpt",
        label: "How to schedule social media posts with ChatGPT",
      },
    ],
    title: "Connect PostDaily to ChatGPT",
    excerpt:
      "Turn on developer mode in ChatGPT, add PostDaily with its server URL, approve access, and draft, schedule and publish social posts from a ChatGPT chat.",
    category: "AI agents",
    updated: "2026-10-02",
    read: "3 min",
    body: [
      {
        type: "p",
        text: "PostDaily isn't in ChatGPT's app directory yet, so you add it yourself with developer mode. It takes about two minutes. OpenAI decides who can use developer mode, and it can depend on your plan and your workspace's settings. If you don't see the option, ask your workspace admin.",
      },
      { type: "h2", text: "Turn on developer mode" },
      {
        type: "steps",
        start: 1,
        items: [
          { title: "In ChatGPT, open **Settings**" },
          {
            title: "Select **Security and login**",
          },
          {
            title: "Turn on **Developer mode**",
            text: "On a Business, Enterprise or Edu workspace, an admin may have to allow it first, or create the app for the whole workspace.",
          },
        ],
      },
      { type: "h2", text: "Add PostDaily" },
      {
        type: "steps",
        start: 4,
        items: [
          {
            title: "Open **Plugins** and select **+**",
          },
          {
            title: "Name it PostDaily",
            text: 'Add a short description, such as "Schedule and publish my social posts". ChatGPT shows it when it decides which tool to use.',
          },
          {
            title: "Under **Connection**, enter the server URL",
            text: "Use **https://mcp.postdaily.app/mcp** and create the connection.",
          },
          {
            title: "Sign in to PostDaily when ChatGPT asks",
            text: "PostDaily opens. Choose **Full access**, **Drafts only** or **Read only**, tick the workspaces ChatGPT may use and select **Allow access**.",
          },
        ],
      },
      { type: "h2", text: "Use it in a chat" },
      {
        type: "p",
        text: 'Start a new conversation and add PostDaily from the tools menu (the **+** next to the message box). Then ask, for example, "List my PostDaily channels" or "Draft three posts about our new menu for Instagram and schedule them for next week."',
      },
      {
        type: "note",
        tone: "info",
        title: "ChatGPT asks before anything goes out right away",
        text: "Even with full access, ChatGPT has to get your go-ahead in the chat before it publishes a post immediately or retries a failed one. Scheduled posts wait in your queue, where you can still edit or cancel them.",
      },
      { type: "h2", text: "Photos and videos" },
      {
        type: "p",
        text: "ChatGPT can't pass a file from your phone or computer straight to PostDaily. Ask it for an upload link: it opens your PostDaily media library in the right workspace. Upload your images, videos or PDFs there, tell ChatGPT you're done, and it attaches them to your post.",
      },
      { type: "h2", text: "If something doesn't look right" },
      {
        type: "ul",
        items: [
          "**OpenAI moves these menus often.** These steps were checked on 2 October 2026. If a label has changed, look for developer mode in ChatGPT's settings, then a way to add an app or plugin by URL.",
          "**ChatGPT says it can't find a channel.** It only sees the workspaces you ticked. Disconnect it in **Settings → AI agents** in PostDaily and connect again to choose different ones.",
          "**To disconnect,** open **Settings → AI agents** in PostDaily and select **Disconnect** next to ChatGPT. It stops working on its next request.",
        ],
      },
    ],
    related: ["connect-ai-apps", "connect-claude", "connect-codex"],
  },
  {
    slug: "connect-cursor",
    links: [
      {
        to: "/ai-agents",
        label: "What you can ask an AI app to do in PostDaily",
      },
    ],
    title: "Connect PostDaily to Cursor",
    excerpt:
      "Add PostDaily to Cursor in one click or with mcp.json, sign in from Cursor's MCPs settings, and have Cursor's agent draft and schedule your release posts.",
    category: "AI agents",
    updated: "2026-10-02",
    read: "3 min",
    body: [
      {
        type: "p",
        text: "Cursor connects with PostDaily's server URL and signs in through PostDaily, so you never paste a key into your editor. It's a natural fit for announcing what you ship: Cursor's agent can read the change you just made and turn it into a post for each network.",
      },
      { type: "h2", text: "Add the server" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title:
              "In PostDaily, open **Settings → AI agents** and choose **Cursor**",
            text: "Select **Add to Cursor**. Cursor opens and asks you to install the postdaily server.",
          },
          {
            title: "Or add it to your config yourself",
            text: 'Add this to `~/.cursor/mcp.json`: `{"mcpServers": {"postdaily": {"url": "https://mcp.postdaily.app/mcp"}}}`',
          },
          {
            title: "Open **Cursor Settings → Customize → MCPs**",
            text: "Select **Authenticate** next to **postdaily**. In older versions of Cursor the page is called **Tools & MCPs**.",
          },
          {
            title: "Approve access in PostDaily",
            text: "PostDaily opens in your browser. Choose **Full access**, **Drafts only** or **Read only**, tick the workspaces and select **Allow access**.",
          },
        ],
      },
      { type: "h2", text: "Use it" },
      {
        type: "p",
        text: 'Ask Cursor\'s agent in chat. For example: "Write a short post about the feature in this branch for Bluesky and X and schedule it for tomorrow at 9am." Cursor can upload a file from your project or computer directly, such as a screenshot or a demo video, so you only have to name the file.',
      },
      {
        type: "note",
        tone: "info",
        title: "It asks before anything goes out right away",
        text: "Cursor has to get your go-ahead in the chat before it publishes a post immediately or retries a failed one. Scheduled posts wait in your queue.",
      },
      { type: "h2", text: "Using an API key instead" },
      {
        type: "p",
        text: "Cursor ignores request headers on the main server URL once it sees that it can sign in. To use an API key, point Cursor at **https://mcp.postdaily.app/key/mcp** instead and add an `Authorization` header with `Bearer` followed by your key. See [Connect n8n, Make or a script with an API key](/help/connect-with-an-api-key) for creating one.",
      },
      { type: "h2", text: "Sign out" },
      {
        type: "p",
        text: "In Cursor, open **postdaily** under **Customize → MCPs**, select **Configure**, then **Logout**. To revoke its access completely, select **Disconnect** next to Cursor in **Settings → AI agents** in PostDaily.",
      },
    ],
    related: ["connect-ai-apps", "connect-claude-code", "connect-codex"],
  },
  {
    slug: "connect-claude-code",
    title: "Use PostDaily in Claude Code",
    excerpt:
      "Install PostDaily's Claude Code plugin, sign in with /mcp, and let Claude Code turn a release into scheduled posts, with a check before anything publishes.",
    category: "AI agents",
    updated: "2026-10-02",
    read: "3 min",
    body: [
      {
        type: "p",
        text: "PostDaily's plugin for Claude Code adds the server plus three skills: one for writing and scheduling posts, one for uploading files from your computer, and one for turning a release into posts. It also asks you before anything publishes right away, deletes or cancels a post, or schedules a batch.",
      },
      { type: "h2", text: "Install the plugin" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Add PostDaily's plugin marketplace",
            text: "In your terminal, run `claude plugin marketplace add mehedihasan2810/postdaily-claude-plugin`",
          },
          {
            title: "Install the plugin",
            text: "Run `claude plugin install postdaily@postdaily`",
          },
          {
            title: "Sign in",
            text: "In Claude Code, run `/mcp`, choose **plugin:postdaily:postdaily** and authenticate. PostDaily opens in your browser: choose what Claude Code can do and which workspaces it can use, then select **Allow access**.",
          },
        ],
      },
      { type: "h2", text: "Or add only the server" },
      {
        type: "p",
        text: "If you'd rather not install the plugin, run `claude mcp add --transport http postdaily https://mcp.postdaily.app/mcp`, then sign in from `/mcp`. You get the same tools without the skills or the extra check before publishing. PostDaily still requires a go-ahead before it publishes right away.",
      },
      { type: "h2", text: "Things to ask" },
      {
        type: "ul",
        items: [
          '"Read the changelog for the release I just tagged and schedule a post about it on Bluesky, Mastodon and X."',
          '"Upload `docs/demo.mp4` and draft a TikTok and a YouTube Short with it."',
          '"What\'s in my queue this week? Fill the empty slots with tips from our docs."',
        ],
      },
      { type: "h2", text: "Disconnect" },
      {
        type: "p",
        text: "In PostDaily, open **Settings → AI agents** and select **Disconnect** next to Claude Code. It stops working on its next request.",
      },
    ],
    related: ["connect-ai-apps", "connect-claude", "connect-codex"],
  },
  {
    slug: "connect-codex",
    title: "Use PostDaily in Codex",
    excerpt:
      "Install PostDaily's Codex plugin, sign in once, and have Codex draft and schedule posts from your terminal or IDE, approving each call that could publish.",
    category: "AI agents",
    updated: "2026-10-02",
    read: "2 min",
    body: [
      {
        type: "p",
        text: "PostDaily's plugin for Codex adds the server plus skills for uploading files from your computer and turning a release into posts. Codex asks you before every call that can publish, schedule, delete or cancel a post. The same setup works in the Codex CLI and the Codex IDE extension.",
      },
      { type: "h2", text: "Install the plugin" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Add PostDaily's plugin marketplace",
            text: "In your terminal, run `codex plugin marketplace add mehedihasan2810/postdaily-codex-plugin`",
          },
          {
            title: "Install the plugin",
            text: "Run `codex plugin add postdaily@postdaily`",
          },
          {
            title: "Sign in",
            text: "Run `codex mcp login postdaily`. PostDaily opens in your browser: choose what Codex can do and which workspaces it can use, then select **Allow access**.",
          },
        ],
      },
      { type: "h2", text: "Or add only the server" },
      {
        type: "p",
        text: "Run `codex mcp add postdaily --url https://mcp.postdaily.app/mcp`, then `codex mcp login postdaily`. You get the same tools without the skills.",
      },
      { type: "h2", text: "Things to ask" },
      {
        type: "ul",
        items: [
          '"Summarise this week\'s merged pull requests as a short post for Bluesky and Threads, and put it in my queue."',
          '"Upload the screenshot in `assets/launch.png` and draft an Instagram post with it."',
          '"Which of my posts failed this week, and why?"',
        ],
      },
      { type: "h2", text: "Disconnect" },
      {
        type: "p",
        text: "In PostDaily, open **Settings → AI agents** and select **Disconnect** next to Codex. It stops working on its next request.",
      },
    ],
    related: ["connect-ai-apps", "connect-claude-code", "connect-cursor"],
  },
  {
    slug: "connect-with-an-api-key",
    title: "Connect n8n, Make or a script with an API key",
    excerpt:
      "Create a PostDaily API key for n8n, Make, Cline, JetBrains AI or your own code, connect it to the key-only MCP URL, and revoke it the moment it leaks.",
    category: "AI agents",
    updated: "2026-10-02",
    read: "3 min",
    body: [
      {
        type: "p",
        text: "Most AI apps sign in with PostDaily. Automations and a few apps can't: n8n, Make, your own scripts, Cline and JetBrains AI Assistant. They use an API key and a separate server URL, **https://mcp.postdaily.app/key/mcp**.",
      },
      { type: "h2", text: "Create a key" },
      {
        type: "steps",
        start: 1,
        items: [
          {
            title: "Open **Settings → AI agents** and select **API key**",
          },
          {
            title: "Name it, and choose what it can do",
            text: 'Use a name you\'ll recognise later, such as "n8n weekly digest". Pick **Full access**, **Drafts only** or **Read only**, and only the workspaces the automation needs.',
          },
          {
            title: "Choose when it expires",
            text: "30 days, 90 days, 1 year or never. A shorter life limits the damage if it leaks.",
          },
          {
            title: "Copy the key straight away",
            text: "It's shown only once. Store it in your automation's credentials or a password manager.",
          },
        ],
      },
      { type: "h2", text: "Connect your tool" },
      {
        type: "ul",
        items: [
          "**URL:** https://mcp.postdaily.app/key/mcp",
          "**Header:** `Authorization: Bearer` followed by your key",
          "**Transport:** Streamable HTTP",
          "**n8n and Make:** use their MCP client step with the three settings above.",
          "**Cline:** add a server with type `streamableHttp`. Without it, Cline uses the older SSE transport, which PostDaily doesn't support.",
          "**JetBrains AI Assistant:** it can't sign in to remote servers yet, so it connects through `mcp-remote` with the key. **Settings → AI agents → JetBrains AI** has the exact entry to paste.",
        ],
      },
      {
        type: "note",
        tone: "warning",
        title: "Treat a key like a password",
        text: "Anyone with the key can act in the workspaces you chose. If one leaks, revoke it in Settings → AI agents. It stops working on its next request.",
      },
      { type: "h2", text: "Good habits for automations" },
      {
        type: "ul",
        items: [
          "**Give each automation its own key**, so revoking one doesn't break the others, and **Settings → AI agents** shows which one was used last.",
          "**Use Drafts only** for anything that writes posts unattended. A person then schedules them in PostDaily after checking them.",
          "**Send an idempotency key with every write.** If your workflow retries a step, PostDaily recognises the repeat and doesn't create the post twice.",
        ],
      },
      { type: "h2", text: "When a key stops working" },
      {
        type: "p",
        text: "A key stops working when it expires, when you revoke it, and when you change or reset your PostDaily password, which revokes every key you created. Create a new one and update your automation.",
      },
    ],
    related: ["connect-ai-apps", "connect-cursor", "two-factor-authentication"],
  },
];

export const HELP_ARTICLES: HelpArticle[] = [
  ...FACEBOOK_ARTICLES,
  ...PRODUCT_ARTICLES,
  ...AI_AGENT_ARTICLES,
  ...TROUBLESHOOTING_ARTICLES,
];

export function getHelpArticle(slug: string): HelpArticle | undefined {
  return HELP_ARTICLES.find((article) => article.slug === slug);
}

export function helpArticlesByCategory(category: HelpCategory): HelpArticle[] {
  return HELP_ARTICLES.filter((article) => article.category === category);
}

export function relatedHelpArticles(article: HelpArticle): HelpArticle[] {
  return (article.related ?? [])
    .map((slug) => getHelpArticle(slug))
    .filter((entry): entry is HelpArticle => Boolean(entry));
}

/**
 * Free-text search over the help index. Every whitespace-separated token has to
 * match somewhere in the title, excerpt or category, so "facebook queue"
 * narrows rather than widening the way an OR match would.
 */
export function searchHelpArticles(query: string): HelpArticle[] {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) {
    return [];
  }
  return HELP_ARTICLES.filter((article) => {
    const haystack =
      `${article.title} ${article.excerpt} ${article.category}`.toLowerCase();
    return tokens.every((token) => haystack.includes(token));
  });
}
